import React from "react";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  IconButton,
  ListItemButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import PlaceIcon from "@mui/icons-material/Place";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import { resolveLandingAssetUrl } from "../../../utils/landingAssets";
import {
  normalizeImagePosition,
  heroTitleVisualScale,
  normalizeVisualStyle,
  segmentCssVariables,
  segmentVisualScale,
  visualStyleFromTemplate,
} from "../landingStudio/segmentVisualStyle";
import {
  clampHeroTextFontScale,
  heroEyebrowFontSize,
  heroSubtitleFontSize,
  heroTitleFontSize,
  scaleResponsiveCss,
  titleWordFitScale,
} from "../landingStudio/heroTextStyle";
import { youtubeEmbedUrl } from "./youtube";
import { normalizeCalendlyUrl } from "../../../utils/calendly";
import { capitalizeSectionTitle } from "../../../utils/landingSectionText";
import {
  applyLandingSegmentSnapshot,
  canonicalLandingSegmentId,
  getLandingSegments,
  isCanonicalLandingSegment,
  isStructuralLandingType,
} from "./landingLayout";
import { resolveSiteExperienceDefaults } from "./siteExperienceDefaults";
import {
  getLandingPreviewDevice,
  getLandingPreviewHeroMinHeight,
  resolveLandingPreviewResponsiveValue,
} from "../landingStudio/previewDevices";
import {
  navbarActionOwner,
  navbarBusinessNameColor,
  navbarCustomButtonOwner,
  navbarLinkOwner,
  navbarLogoSurfaceOwner,
  navbarMobileOwner,
  navbarSurfaceOwner,
} from "../landingStudio/navbarVisualOwnership";
import { materializeMegaMenuVisual } from "../landingStudio/megaMenuVisualOwnership";
import { HIGHLIGHT_VISUAL_DEFAULTS } from "../landingStudio/highlightVisualOwnership";
import {
  floatingWhatsappButtonSx,
  normalizeFloatingWhatsappStyle,
} from "../landingStudio/floatingWhatsappVisualOwnership";
import { normalizeCustomSegmentStyle, customShadow } from "../landingStudio/customVisualOwnership";
import { isSupportedMapEmbedUrl, mapEmbedUrlFromInput } from "../landingStudio/mapEmbed";
import {
  HeroCanvasCtaIcon,
  HighlightCanvasIcon,
  SafeCanvasImage,
  LANDING_CANVAS_RENDER_DEFAULTS,
  finite,
  px,
  normalizeHeroHorizontalPosition,
  normalizeHeroVerticalPosition,
  heroHorizontalFlex,
  heroVerticalFlex,
  colorWithAlpha,
  SectionFrame,
  LandingPreviewHorizontalTrack,
  PlaceholderCards,
} from "./canvas/LandingCanvasPrimitives";


function heroTextLengthSafe(value) {
  return String(value ?? "").trim().length;
}

function configuredStyleValue(style, key) {
  return style && Object.prototype.hasOwnProperty.call(style, key) && style[key] !== "" && style[key] !== null && style[key] !== undefined;
}

function optionalBoundedNumber(value, min, max) {
  if (value === "" || value === null || value === undefined) return null;
  const number = Number(value);
  if (!Number.isFinite(number)) return null;
  return Math.min(max, Math.max(min, number));
}

function cssColorWithOpacity(color, opacity = 1) {
  const candidate = String(color || "").trim();
  const alpha = Math.max(0, Math.min(1, Number.isFinite(Number(opacity)) ? Number(opacity) : 1));
  if (!candidate || candidate.toLowerCase() === "transparent" || alpha <= 0) return "transparent";
  if (alpha >= 0.999) return candidate;
  const match = candidate.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (!match) return candidate;
  const raw = match[1].length === 3
    ? match[1].split("").map((char) => `${char}${char}`).join("")
    : match[1];
  const red = Number.parseInt(raw.slice(0, 2), 16);
  const green = Number.parseInt(raw.slice(2, 4), 16);
  const blue = Number.parseInt(raw.slice(4, 6), 16);
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

function explicitVisualShadow(style = {}, hover = false) {
  const prefix = hover ? "hoverShadow" : "shadow";
  const opacity = Number(style?.[`${prefix}Opacity`]);
  if (!Number.isFinite(opacity) || opacity <= 0) return "none";
  const color = cssColorWithOpacity(style?.[`${prefix}Color`] || "#000000", opacity);
  const x = Number(style?.[`${prefix}X`]) || 0;
  const y = Number(style?.[`${prefix}Y`]) || 0;
  const blur = Math.max(0, Number(style?.[`${prefix}Blur`]) || 0);
  const spread = Number(style?.[`${prefix}Spread`]) || 0;
  return `${x}px ${y}px ${blur}px ${spread}px ${color}`;
}

function heroCtaCanvasSx(style, { outlined, colors, fallbackShadow }) {
  const current = style && typeof style === "object" ? style : {};
  const hasCustom = Object.keys(current).some((key) => configuredStyleValue(current, key));
  if (!hasCustom) {
    return outlined
      ? { color: colors.onSecondary, borderColor: colors.onSecondary, textTransform: "none" }
      : { bgcolor: colors.primary, color: colors.onPrimary, textTransform: "none", boxShadow: fallbackShadow };
  }
  const background = String(current.backgroundColor || "").trim() || (outlined ? "#ffffff" : colors.primary);
  const hoverBackground = String(current.hoverBackgroundColor || "").trim() || (outlined ? "#ffffff" : colors.secondary);
  const textColor = String(current.textColor || "").trim() || (outlined ? colors.onSecondary : colors.onPrimary);
  const hoverTextColor = String(current.hoverTextColor || "").trim() || textColor;
  const borderColor = String(current.borderColor || "").trim() || colors.onSecondary;
  const borderWidth = configuredStyleValue(current, "borderWidth") ? Number(current.borderWidth) : (outlined ? 1 : 0);
  const shadowOpacity = configuredStyleValue(current, "shadowOpacity") ? Number(current.shadowOpacity) : (outlined ? 0 : 0.28);
  const shadowColor = String(current.shadowColor || "").trim() || "#000000";
  const shadow = shadowOpacity <= 0
    ? "none"
    : `${configuredStyleValue(current, "shadowX") ? Number(current.shadowX) : 0}px ${configuredStyleValue(current, "shadowY") ? Number(current.shadowY) : (outlined ? 0 : 10)}px ${configuredStyleValue(current, "shadowBlur") ? Number(current.shadowBlur) : (outlined ? 0 : 28)}px ${configuredStyleValue(current, "shadowSpread") ? Number(current.shadowSpread) : 0}px ${cssColorWithOpacity(shadowColor, shadowOpacity)}`;
  const sx = {
    textTransform: "none",
    color: textColor,
    bgcolor: cssColorWithOpacity(background, configuredStyleValue(current, "backgroundOpacity") ? current.backgroundOpacity : (outlined ? 0.08 : 1)),
    borderStyle: borderWidth > 0 ? "solid" : "none",
    borderWidth: `${borderWidth}px`,
    borderColor,
    boxShadow: shadow,
    "&:hover": {
      color: hoverTextColor,
      bgcolor: cssColorWithOpacity(hoverBackground, configuredStyleValue(current, "hoverBackgroundOpacity") ? current.hoverBackgroundOpacity : (outlined ? 0.18 : 1)),
      borderColor,
    },
  };
  if (configuredStyleValue(current, "borderRadius")) sx.borderRadius = `${Number(current.borderRadius)}px`;
  if (configuredStyleValue(current, "fontSize")) sx.fontSize = `${Number(current.fontSize)}px`;
  if (configuredStyleValue(current, "fontWeight")) sx.fontWeight = current.fontWeight;
  if (configuredStyleValue(current, "minWidth")) sx.minWidth = `${Number(current.minWidth)}px`;
  if (configuredStyleValue(current, "minHeight")) sx.minHeight = `${Number(current.minHeight)}px`;
  if (configuredStyleValue(current, "paddingX")) sx.px = `${Number(current.paddingX)}px`;
  if (configuredStyleValue(current, "paddingY")) sx.py = `${Number(current.paddingY)}px`;
  return sx;
}

// Navbar preview styles intentionally mirror GoodBuyShop's NavbarDesktopNavigation,
// NavbarActions and NavbarMobileDrawer. The editor must interpret the explicit
// navigation owners exactly like the storefront; otherwise a saved color can
// legitimately look different before/after publishing.
function navbarCanvasDesktopLinkSx(style = {}, active = false) {
  return {
    position: "relative",
    minHeight: 42,
    px: { md: 1.05, lg: 1.55 },
    borderRadius: 999,
    color: active ? style.activeTextColor : style.textColor,
    bgcolor: active ? style.activeBackgroundColor : style.backgroundColor,
    border: "1px solid",
    borderColor: active ? style.activeBorderColor : style.borderColor,
    fontWeight: active ? 900 : 750,
    letterSpacing: "-.01em",
    minWidth: 0,
    maxWidth: { md: 154, lg: 202 },
    whiteSpace: "nowrap",
    textTransform: "none",
    "&:hover": {
      color: style.hoverTextColor,
      bgcolor: style.hoverBackgroundColor,
      borderColor: style.hoverBorderColor,
    },
    "& .MuiButton-endIcon": {
      color: active ? style.activeTextColor : (style.endIconColor || style.textColor),
    },
    "&::after": active
      ? {
          content: '""',
          position: "absolute",
          left: 18,
          right: 18,
          bottom: 5,
          height: 2,
          borderRadius: 99,
          bgcolor: style.activeIndicatorColor,
        }
      : undefined,
  };
}

function navbarCanvasActionSx(style = {}, compact = false) {
  return {
    width: compact ? { xs: 38, sm: 42 } : 44,
    height: compact ? { xs: 38, sm: 42 } : 44,
    color: style.color,
    bgcolor: style.backgroundColor,
    border: "1px solid",
    borderColor: style.borderColor,
    boxShadow: compact ? "none" : "0 5px 16px rgba(20,25,35,.06)",
    "&:hover": {
      color: style.hoverColor,
      bgcolor: style.hoverBackgroundColor,
      borderColor: style.hoverBorderColor,
    },
  };
}

function navbarCanvasLoginSx(style = {}) {
  return {
    minHeight: 42,
    px: 1.7,
    borderRadius: 999,
    color: style.color,
    bgcolor: style.backgroundColor,
    border: "1px solid",
    borderColor: style.borderColor,
    fontWeight: 800,
    whiteSpace: "nowrap",
    textTransform: "none",
    "&:hover": {
      color: style.hoverColor,
      bgcolor: style.hoverBackgroundColor,
      borderColor: style.hoverBorderColor,
    },
  };
}

function navbarCanvasMobileLinkSx(style = {}, active = false) {
  return {
    minHeight: style.minHeight ?? 52,
    px: 1.5,
    borderRadius: `${style.borderRadius ?? 18}px`,
    color: active ? style.activeTextColor : style.textColor,
    bgcolor: active ? style.activeBackgroundColor : style.backgroundColor,
    border: "1px solid",
    borderColor: active ? style.activeBorderColor : style.borderColor,
    "&:hover": {
      bgcolor: style.hoverBackgroundColor,
      color: style.hoverTextColor,
      borderColor: style.hoverBorderColor,
    },
    "&:focus-visible": {
      outline: `3px solid ${style.focusColor || "#F35A3A"}`,
      outlineOffset: 2,
    },
  };
}

function NavbarCanvasMobileItem({ label, fieldPath = "", style = {}, active = false, endIcon = null, buttonId = "" }) {
  const safeLabel = String(label ?? "").trim();
  if (!safeLabel) return null;
  return (
    <ListItemButton
      data-landing-field={fieldPath || undefined}
      data-navbar-button-id={buttonId || undefined}
      data-landing-element-type="button"
      sx={navbarCanvasMobileLinkSx(style, active)}
    >
      <Typography
        title={safeLabel}
        sx={{
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          fontSize: `${style.fontSize ?? 16}px`,
          fontWeight: active ? (style.activeFontWeight ?? 900) : (style.fontWeight ?? 800),
        }}
      >
        {safeLabel}
      </Typography>
      <Box sx={{ display: "flex", flexShrink: 0, color: style.endIconColor }}>
        {endIcon}
      </Box>
    </ListItemButton>
  );
}


function ImageCarouselCanvasPreview({
  items = [],
  config = {},
  mobileLayout = false,
  responsive,
  studioMode = false,
}) {
  const [activePosition, setActivePosition] = React.useState(0);

  React.useEffect(() => {
    setActivePosition((current) => Math.min(Math.max(0, current), Math.max(0, items.length - 1)));
  }, [items.length]);

  React.useEffect(() => {
    if (studioMode || config.autoplay === false || items.length < 2) return undefined;
    const interval = Math.min(15000, Math.max(2000, Number(config.intervalMs) || 4500));
    const timer = window.setInterval(() => {
      setActivePosition((current) => (current + 1) % items.length);
    }, interval);
    return () => window.clearInterval(timer);
  }, [config.autoplay, config.intervalMs, items.length, studioMode]);

  if (!items.length) return null;

  const safePosition = Math.min(activePosition, items.length - 1);
  const current = items[safePosition];
  const mediaStyle = config.mediaStyle || {};
  const captionStyle = config.captionStyle || {};
  const arrowStyle = config.arrowStyle || {};
  const dotStyle = config.dotStyle || {};
  const height = mobileLayout ? finite(mediaStyle.heightMobile, 280) : finite(mediaStyle.heightDesktop, 460);
  const goTo = (event, next) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    setActivePosition((next + items.length) % items.length);
  };

  return (
    <Box
      sx={{
        position: "relative",
        height,
        borderRadius: `${finite(mediaStyle.borderRadius, 28)}px`,
        overflow: "hidden",
        bgcolor: mediaStyle.backgroundColor || "rgba(25,36,47,.06)",
        border: `${finite(mediaStyle.borderWidth, 0)}px solid ${mediaStyle.borderColor || "transparent"}`,
        boxShadow: mediaStyle.boxShadow || "none",
      }}
    >
      {current.imageUrl ? (
        <SafeCanvasImage
          src={current.imageUrl}
          alt={current.item?.imageAlt || ""}
          landingField={studioMode ? `imageCarousel.items.${current.index}.imageUrl` : ""}
          landingElementType="image"
          editLabel={`Editar imagen ${safePosition + 1}`}
          sx={{
            width: "100%",
            height: "100%",
            display: "block",
            objectFit: "cover",
            objectPosition: normalizeImagePosition(current.item?.imagePosition),
          }}
        />
      ) : (
        <Box
          data-landing-field={studioMode ? `imageCarousel.items.${current.index}` : undefined}
          data-landing-element-type={studioMode ? "collection-item" : undefined}
          data-landing-edit-label={studioMode ? `Editar imagen ${safePosition + 1} del carrusel` : undefined}
          sx={{ width: "100%", height: "100%", display: "grid", placeItems: "center", bgcolor: "rgba(25,36,47,.06)" }}
        >
          <Typography sx={{ color: "rgba(25,36,47,.55)", fontWeight: 800 }}>Cargá una imagen</Typography>
        </Box>
      )}

      {current.item?.caption ? (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "flex-end",
            p: responsive({ xs: 2.2, md: 3.5 }, 2.2),
            background: `linear-gradient(180deg, transparent 35%, ${captionStyle.overlayColor || "rgba(0,0,0,.38)"} 100%)`,
            pointerEvents: "none",
          }}
        >
          <Typography
            data-landing-field={studioMode ? `imageCarousel.items.${current.index}.caption` : undefined}
            data-landing-element-type={studioMode ? "text" : undefined}
            data-landing-edit-label={studioMode ? "Editar epígrafe" : undefined}
            sx={{
              color: captionStyle.color || "#FFFFFF",
              fontWeight: finite(captionStyle.fontWeight, 800),
              fontSize: responsive({ xs: finite(captionStyle.fontSizeMobile, 17), md: finite(captionStyle.fontSizeDesktop, 22) }, finite(captionStyle.fontSizeMobile, 17)),
              lineHeight: finite(captionStyle.lineHeight, 1.35),
              maxWidth: finite(captionStyle.maxWidth, 900),
              pointerEvents: studioMode ? "auto" : "none",
            }}
          >
            {current.item.caption}
          </Typography>
        </Box>
      ) : null}

      {config.showArrows !== false && items.length > 1 ? (
        <>
          <IconButton
            aria-label="Imagen anterior"
            onClick={(event) => goTo(event, safePosition - 1)}
            sx={{
              position: "absolute",
              left: responsive({ xs: 10, md: 18 }, 10),
              top: "50%",
              transform: "translateY(-50%)",
              width: finite(arrowStyle.size, 40),
              height: finite(arrowStyle.size, 40),
              bgcolor: arrowStyle.backgroundColor || "rgba(255,255,255,.92)",
              color: arrowStyle.color || "#19242F",
              border: `1px solid ${arrowStyle.borderColor || "transparent"}`,
              boxShadow: arrowStyle.boxShadow || "none",
              "&:hover": { bgcolor: arrowStyle.hoverBackgroundColor || "#FFFFFF", color: arrowStyle.hoverColor || "#19242F", borderColor: arrowStyle.hoverBorderColor || "transparent" },
              "&.Mui-focusVisible": { outline: `2px solid ${arrowStyle.focusColor || "#F35A3A"}`, outlineOffset: 2 },
            }}
          >
            <ChevronRightRoundedIcon sx={{ transform: "rotate(180deg)" }} />
          </IconButton>
          <IconButton
            aria-label="Imagen siguiente"
            onClick={(event) => goTo(event, safePosition + 1)}
            sx={{
              position: "absolute",
              right: responsive({ xs: 10, md: 18 }, 10),
              top: "50%",
              transform: "translateY(-50%)",
              width: finite(arrowStyle.size, 40),
              height: finite(arrowStyle.size, 40),
              bgcolor: arrowStyle.backgroundColor || "rgba(255,255,255,.92)",
              color: arrowStyle.color || "#19242F",
              border: `1px solid ${arrowStyle.borderColor || "transparent"}`,
              boxShadow: arrowStyle.boxShadow || "none",
              "&:hover": { bgcolor: arrowStyle.hoverBackgroundColor || "#FFFFFF", color: arrowStyle.hoverColor || "#19242F", borderColor: arrowStyle.hoverBorderColor || "transparent" },
              "&.Mui-focusVisible": { outline: `2px solid ${arrowStyle.focusColor || "#F35A3A"}`, outlineOffset: 2 },
            }}
          >
            <ChevronRightRoundedIcon />
          </IconButton>
        </>
      ) : null}

      {config.showDots !== false && items.length > 1 ? (
        <Stack direction="row" spacing={0.7} sx={{ position: "absolute", left: "50%", bottom: responsive({ xs: 12, md: 16 }, 12), transform: "translateX(-50%)", alignItems: "center" }}>
          {items.map((entry, dotIndex) => (
            <Box
              component="button"
              type="button"
              key={entry.item?.id || `${entry.index}:${dotIndex}`}
              aria-label={`Ver imagen ${dotIndex + 1}`}
              aria-current={dotIndex === safePosition ? "true" : undefined}
              onClick={(event) => goTo(event, dotIndex)}
              sx={{
                width: dotIndex === safePosition ? finite(dotStyle.activeWidth, 22) : finite(dotStyle.width, 8),
                height: finite(dotStyle.height, 8),
                p: 0,
                border: 0,
                borderRadius: 999,
                bgcolor: dotIndex === safePosition
                  ? (dotStyle.activeColor || "#FFFFFF")
                  : (dotStyle.color || "rgba(255,255,255,.55)"),
                cursor: "pointer",
                transition: "width 140ms ease, background-color 140ms ease",
                "&:hover": { bgcolor: dotIndex === safePosition ? (dotStyle.activeHoverColor || "#FFFFFF") : (dotStyle.hoverColor || "#FFFFFF") },
              }}
            />
          ))}
        </Stack>
      ) : null}

      {studioMode && items.length > 1 ? (
        <Chip
          size="small"
          label={`${safePosition + 1} / ${items.length}`}
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            bgcolor: "rgba(25,36,47,.72)",
            color: "#fff",
            fontWeight: 900,
            pointerEvents: "none",
          }}
        />
      ) : null}
    </Box>
  );
}

export default function LandingCanvas({
  form = {},
  activeSection = "",
  selectedElement = null,
  device = "desktop",
  productsEnabled = true,
  gastronomicMenuEnabled = false,
  onSelectSection,
  onSelectElement,
  studioMode = false,
  onOpenSegmentTool,
  onToggleSection,
  onRemoveSection,
  onlySection = "",
  bare = false,
  layoutInstanceId = "",
}) {
  const displayForm = resolveSiteExperienceDefaults(form, {
    gastronomicMenuEnabled,
    productsEnabled,
  });
  const business = displayForm.business || {};
  const navigation = displayForm.navigation || {};
  const previewDevice = getLandingPreviewDevice(device);
  const desktopLayout = previewDevice.id === "desktop";
  const mobileLayout = previewDevice.id === "mobile";
  const megaMenuVisual = materializeMegaMenuVisual({ ...navigation, megaMenuStyle: "" }).megaMenuVisual;
  const selectedNavbarPath = String(selectedElement?.fieldPath || "");
  const selectedNavbarButtonMatch = selectedNavbarPath.match(/^navigation\.buttons\.(\d+)\.label$/);
  const selectedNavbarButton = selectedNavbarButtonMatch ? navigation.buttons?.[Number(selectedNavbarButtonMatch[1])] : null;
  const megaMenuPreviewActive = studioMode && desktopLayout && selectedElement?.sectionId === "navbar" && (
    selectedNavbarPath === "navigation.productsLabel" ||
    selectedNavbarPath.startsWith("navigation.megaMenu") ||
    selectedNavbarButton?.behavior === "mega-menu"
  );
  const megaMenuPreviewVariant = ["elegant", "catalog", "showcase", "minimal", "editorial"].includes(navigation.megaMenuStyle)
    ? navigation.megaMenuStyle
    : "catalog";
  const megaMenuPreviewColumns = navigation.megaMenuLayout === "stacked"
    ? 1
    : ["2", "3", "4"].includes(String(navigation.megaMenuColumns || ""))
      ? Number(navigation.megaMenuColumns)
      : 2;
  const megaMenuPreviewState = selectedNavbarPath.startsWith("navigation.megaMenuVisual.loading.")
    ? "loading"
    : selectedNavbarPath.startsWith("navigation.megaMenuVisual.empty.")
      ? "empty"
      : selectedNavbarPath.startsWith("navigation.megaMenuVisual.error.")
        ? "error"
        : "content";
  const hero = displayForm.hero || {};
  const catalog = displayForm.catalog || {};
  const gastronomicHome = gastronomicMenuEnabled && catalog.homeSource === "gastronomicMenu";
  const catalogCards = catalog.cards || {};
  const about = displayForm.about || {};
  const highlightsIntro = displayForm.highlightsIntro || {};
  const highlights = Array.isArray(displayForm.highlights) ? displayForm.highlights : [];
  const imageCarousel = displayForm.imageCarousel || {};
  const testimonials = displayForm.testimonials || {};
  const contact = displayForm.contact || {};
  const custom = displayForm.custom || {};
  const footer = displayForm.footer || {};
  const sections = displayForm.sections || {};
  const widgets = displayForm.widgets || {};
  const floatingWhatsappStyle = normalizeFloatingWhatsappStyle(widgets.floatingWhatsapp?.style);
  const floatingWhatsappVisualSx = floatingWhatsappButtonSx(floatingWhatsappStyle);
  const theme = displayForm.theme || {};
  const layoutSegments = getLandingSegments(displayForm);
  const segmentAllowed = (type) => onlySection
    ? onlySection === type
    : layoutSegments.some((segment) => segment.type === type && isCanonicalLandingSegment(segment));
  const orderFor = (type) => {
    const targetId = layoutInstanceId || type;
    const index = layoutSegments.findIndex((segment) => segment.id === targetId);
    return index >= 0 ? index : layoutSegments.length;
  };
  const sectionBackgroundSx = (type) => {
    const targetId = layoutInstanceId || type;
    const segment = layoutSegments.find((candidate) => candidate.id === targetId)
      || layoutSegments.find((candidate) => candidate.type === type && candidate.id === canonicalLandingSegmentId(type));
    const imageUrl = resolveLandingAssetUrl(segment?.visualStyle?.backgroundImageUrl);
    if (!imageUrl) return {};
    return {
      backgroundImage: `url("${String(imageUrl).replace(/"/g, "%22")}")`,
      backgroundSize: "cover",
      backgroundPosition: normalizeImagePosition(segment?.visualStyle?.backgroundImagePosition),
      backgroundRepeat: "no-repeat",
    };
  };
  const colors = {
    primary: theme.primaryColor || "#F35A3A",
    secondary: theme.secondaryColor || "#19242F",
    background: theme.backgroundColor || "#EEF0F5",
    surface: theme.surfaceColor || "#FFFFFF",
    text: theme.textColor || "#1A1A2E",
    mutedText: theme.mutedTextColor || "#5B5D73",
    divider: theme.dividerColor || "#E1E4EA",
    onPrimary: "#FFFFFF",
    onSecondary: "#FFFFFF",
  };
  const paletteForSection = (sectionKey) => {
    const targetId = layoutInstanceId || sectionKey;
    const segment = layoutSegments.find((candidate) => candidate.id === targetId)
      || layoutSegments.find((candidate) => candidate.id === sectionKey);
    return segment?.visualStyle?.useGlobalColors === false
      ? { ...colors, ...(segment.visualStyle.colors || {}) }
      : colors;
  };
  const elementColorSxForSection = (sectionKey) => {
    const targetId = layoutInstanceId || sectionKey;
    const segment = layoutSegments.find((candidate) => candidate.id === targetId)
      || layoutSegments.find((candidate) => candidate.id === sectionKey);
    const elementColors = segment?.visualStyle?.elementColors;
    if (!elementColors || typeof elementColors !== "object") return {};
    return Object.entries(elementColors).reduce((sx, [path, rawStyle]) => {
      if (!path || !rawStyle || typeof rawStyle !== "object") return sx;
      const safePath = String(path).replace(/["\\]/g, "\\$&");
      const selector = `& [data-landing-field="${safePath}"], & [data-landing-color-field="${safePath}"]`;
      const normal = {};
      const hover = {};
      if (rawStyle.color) normal.color = `${rawStyle.color} !important`;
      if (rawStyle.background) normal.backgroundColor = `${rawStyle.background} !important`;
      if (rawStyle.borderColor) normal.borderColor = `${rawStyle.borderColor} !important`;
      if (rawStyle.shadow) {
        normal.boxShadow = `0 8px 24px ${rawStyle.shadow} !important`;
        normal.textShadow = `0 2px 10px ${rawStyle.shadow} !important`;
      }
      if (rawStyle.hoverColor) hover.color = `${rawStyle.hoverColor} !important`;
      if (rawStyle.hoverBackground) hover.backgroundColor = `${rawStyle.hoverBackground} !important`;
      if (Object.keys(normal).length) sx[selector] = normal;
      if (Object.keys(hover).length) sx[`${selector}:hover`] = hover;
      return sx;
    }, {});
  };
  const elementFontScaleForSection = (sectionKey, path) => {
    const targetId = layoutInstanceId || sectionKey;
    const segment = layoutSegments.find((candidate) => candidate.id === targetId)
      || layoutSegments.find((candidate) => candidate.id === sectionKey);
    const raw = segment?.visualStyle?.elementColors?.[path]?.fontScale;
    return clampHeroTextFontScale(raw, 1);
  };
  const segmentVisualStyleForSection = (sectionKey) => {
    const targetId = layoutInstanceId || sectionKey;
    return layoutSegments.find((candidate) => candidate.id === targetId)?.visualStyle
      || layoutSegments.find((candidate) => candidate.id === sectionKey)?.visualStyle
      || {};
  };
  const heroColors = paletteForSection("hero");
  const spotlightColors = paletteForSection("spotlight");
  const featuredColors = paletteForSection("featured");
  const brandsColors = paletteForSection("brands");
  const highlightsColors = paletteForSection("highlights");
  const customSegment = layoutSegments.find((candidate) => candidate.id === (layoutInstanceId || "custom"))
    || layoutSegments.find((candidate) => candidate.id === "custom");
  const customSegmentStyle = normalizeCustomSegmentStyle(customSegment?.style);
  const contactColors = paletteForSection("contact");
  const footerColors = paletteForSection("footer");
  const baseRadius = finite(theme.borderRadius, LANDING_CANVAS_RENDER_DEFAULTS.borders.radiusMd);
  const typography = {
    ...LANDING_CANVAS_RENDER_DEFAULTS.typography,
    fontFamily: theme.fontFamily || "Asap",
    headingFontFamily: theme.headingFontFamily || theme.fontFamily || "Exo 2",
    bodyFontFamily: theme.bodyFontFamily || theme.fontFamily || "Asap",
  };
  const spacing = LANDING_CANVAS_RENDER_DEFAULTS.spacing;
  const borders = {
    ...LANDING_CANVAS_RENDER_DEFAULTS.borders,
    radiusSm: Math.max(2, Math.round(baseRadius / 2)),
    radiusMd: baseRadius,
    radiusLg: Math.min(160, baseRadius + 8),
    color: colors.divider,
  };
  const shadows = LANDING_CANVAS_RENDER_DEFAULTS.shadows;
  const responsive = (values, fallback) =>
    resolveLandingPreviewResponsiveValue(previewDevice.id, values, fallback);
  const heroPreviewMinHeight = getLandingPreviewHeroMinHeight(previewDevice.id);

  const heroHorizontalPosition = normalizeHeroHorizontalPosition(
    hero.contentHorizontalAlign ??
      hero.contentHorizontalPosition ??
      hero.textHorizontalPosition ??
      hero.contentPositionHorizontal ??
      hero.horizontalPosition
  );
  const heroVerticalPosition = normalizeHeroVerticalPosition(
    hero.contentVerticalAlign ??
      hero.contentVerticalPosition ??
      hero.textVerticalPosition ??
      hero.contentPositionVertical ??
      hero.verticalPosition
  );
  const heroContentJustify = heroHorizontalFlex(heroHorizontalPosition);
  const heroContentAlign = heroVerticalFlex(heroVerticalPosition);
  const heroTextAlign = heroHorizontalPosition;

  const primary = colors.primary;
  const secondary = colors.secondary;
  const surface = colors.surface;
  const background = colors.background;
  const text = colors.text;
  const muted = colors.mutedText;
  const onPrimary = colors.onPrimary;
  const onSecondary = colors.onSecondary;
  const horizontalPadding = responsive(
    { xs: px(spacing.md, 16), md: px(spacing.xxl, 48) },
    px(spacing.md, 16)
  );
  const cardBorder = `${finite(borders.width, 1)}px ${borders.style || "solid"} ${borders.color || colors.divider}`;
  const categoriesVisual = catalog.categories || {};
  const aboutImageStyle = about.imageStyle || {};
  const aboutCtaStyle = about.ctaStyle || {};
  const aboutVisual = {
    backgroundColor: about.backgroundColor || "#EEF0F5",
    contentBackgroundColor: about.contentBackgroundColor || "transparent",
    contentBorderRadius: finite(about.contentBorderRadius, 40),
    eyebrowColor: about.eyebrowColor || "#F35A3A",
    titleColor: about.titleColor || "#19242F",
    descriptionColor: about.descriptionColor || "#5B5D73",
    descriptionToggleColor: about.descriptionToggleColor || "#F35A3A",
    imageBackgroundColor: aboutImageStyle.backgroundColor || "rgba(25,36,47,.06)",
    imageBorderRadius: finite(aboutImageStyle.borderRadius, 50),
    imageShadow: explicitVisualShadow(aboutImageStyle),
    ctaBackgroundColor: aboutCtaStyle.backgroundColor || "#F35A3A",
    ctaTextColor: aboutCtaStyle.textColor || "#FFFFFF",
    ctaHoverBackgroundColor: aboutCtaStyle.hoverBackgroundColor || "#E05335",
    ctaHoverTextColor: aboutCtaStyle.hoverTextColor || "#EBEBEB",
    ctaBorderColor: aboutCtaStyle.borderColor || "transparent",
    ctaBorderWidth: finite(aboutCtaStyle.borderWidth, 0),
    ctaBorderRadius: finite(aboutCtaStyle.borderRadius, 999),
    ctaShadow: explicitVisualShadow(aboutCtaStyle),
    ctaHoverShadow: explicitVisualShadow(aboutCtaStyle, true),
    ctaFocusRingColor: aboutCtaStyle.focusRingColor || "#F35A3A18",
  };
  aboutVisual.ctaFocusShadow = aboutVisual.ctaShadow === "none"
    ? `0 0 0 4px ${aboutVisual.ctaFocusRingColor}`
    : `0 0 0 4px ${aboutVisual.ctaFocusRingColor}, ${aboutVisual.ctaShadow}`;
  const highlightsVisual = {
    backgroundColor: highlightsIntro.backgroundColor,
    backgroundImageUrl: highlightsIntro.backgroundImageUrl || "",
    backgroundImagePosition: highlightsIntro.backgroundImagePosition || "center",
    backgroundImageOpacity: highlightsIntro.backgroundImageOpacity ?? 0.24,
    eyebrowColor: highlightsIntro.eyebrowColor,
    titleColor: highlightsIntro.titleColor,
    subtitleColor: highlightsIntro.subtitleColor,
  };
  const contactVisual = {
    backgroundColor: contact.backgroundColor || "#19242F",
    imageBackgroundColor: contact.imageBackgroundColor || "rgba(255,255,255,.08)",
    decorativeAccentColor: contact.decorativeAccentColor || "#F35A3A",
    titleColor: contact.titleColor || "#FFFFFF",
    subtitleColor: contact.subtitleColor || "rgba(255,255,255,.82)",
    addressTextColor: contact.addressTextColor || "rgba(255,255,255,.82)",
    scheduleTextColor: contact.scheduleTextColor || "rgba(255,255,255,.82)",
    phoneTextColor: contact.phoneTextColor || "rgba(255,255,255,.82)",
    emailTextColor: contact.emailTextColor || "rgba(255,255,255,.82)",
    addressIconColor: contact.addressIconColor || "#F35A3A",
    scheduleIconColor: contact.scheduleIconColor || "#F35A3A",
    phoneIconColor: contact.phoneIconColor || "#F35A3A",
    emailIconColor: contact.emailIconColor || "#F35A3A",
    detailsCtaTextColor: contact.detailsCtaStyle?.textColor || "#FFFFFF",
  };
  const heroSlideIndex = Array.isArray(hero.slides)
    ? hero.slides.findIndex((slide) => slide?.url)
    : -1;
  const heroSlide = heroSlideIndex >= 0 ? hero.slides[heroSlideIndex] : null;
  const heroImageSourcePath = heroSlide
    ? `hero.slides.${heroSlideIndex}.url`
    : "hero.imageUrl";
  const heroImage = resolveLandingAssetUrl(heroSlide?.url || hero.imageUrl);
  const heroTitle = heroSlide?.title ?? hero.title ?? business.name ?? "";
  const heroSubtitle = heroSlide?.subtitle ?? hero.subtitle ?? "";
  const heroImagePosition = normalizeImagePosition(
    heroSlide?.position,
    normalizeImagePosition(hero.imagePosition)
  );
  const heroTitleFieldPath = heroSlide ? `hero.slides.${heroSlideIndex}.title` : "hero.title";
  const heroSubtitleFieldPath = heroSlide ? `hero.slides.${heroSlideIndex}.subtitle` : "hero.subtitle";
  const heroRotatingPhraseIndex = Array.isArray(hero.rotatingPhrases)
    ? hero.rotatingPhrases.findIndex((phrase) => String(phrase || "").trim())
    : -1;
  const heroRotatingPhrase = heroRotatingPhraseIndex >= 0 ? String(hero.rotatingPhrases[heroRotatingPhraseIndex] || "") : "";
  const heroSlideCount = Array.isArray(hero.slides) ? hero.slides.length : 0;
  const heroPhraseCount = Array.isArray(hero.rotatingPhrases) ? hero.rotatingPhrases.length : 0;
  const showHeroPrimaryCta = Boolean(hero.primaryCtaLabel && hero.primaryCtaUrl) &&
    (!hero.primaryCtaRequiresProducts || productsEnabled);
  const showHeroSecondaryCta = Boolean(hero.secondaryCtaLabel && hero.secondaryCtaUrl) &&
    (!hero.secondaryCtaRequiresProducts || productsEnabled);
  const showAboutCta = Boolean(about.ctaLabel && about.ctaUrl) &&
    (!about.ctaRequiresProducts || productsEnabled);
  const configuredBrands = Array.isArray(catalog.brands?.items)
    ? catalog.brands.items
        .map((item, sourceIndex) => ({ item, sourceIndex }))
        .filter(({ item }) => (item?.name ?? item?.label) || item?.logoUrl)
    : [];
  const logoSourcePath = theme.logoUrl ? "theme.logoUrl" : "theme.compactLogoUrl";
  const logo = resolveLandingAssetUrl(theme.logoUrl || theme.compactLogoUrl);
  const navbarLogo = navigation.showLogo !== false
    ? resolveLandingAssetUrl(
        desktopLayout
          ? theme.logoUrl
          : (theme.compactLogoUrl || theme.logoUrl)
      )
    : "";
  const navbarLogoColor = desktopLayout
    ? String(navigation.logoColor || "").trim()
    : String(navigation.mobile?.logoColor || "").trim();
  const navbarLogoMaxHeight = desktopLayout
    ? optionalBoundedNumber(navigation.logoMaxHeight, 20, 120)
    : optionalBoundedNumber(navigation.mobile?.logoMaxHeight, 20, 96);
  const footerLogoColor = String(footer.logoColor || "").trim();
  const footerLogoMaxHeight = optionalBoundedNumber(footer.logoMaxHeight, 20, 160);
  const aboutImage = resolveLandingAssetUrl(about.imageUrl);
  const highlightsBackground = resolveLandingAssetUrl(highlightsVisual.backgroundImageUrl);
  const highlightsBackgroundPosition = normalizeImagePosition(highlightsVisual.backgroundImagePosition);
  const highlightsBackgroundOpacity = Math.max(0, Math.min(1, finite(highlightsVisual.backgroundImageOpacity, 0.24)));
  const contactImage = resolveLandingAssetUrl(contact.backgroundImageUrl);
  const contactMapUrl = isSupportedMapEmbedUrl(business.mapEmbedUrl)
    ? mapEmbedUrlFromInput(business.mapEmbedUrl)
    : "";
  const contactHasMedia = Boolean(contactMapUrl || contactImage);
  const contactImagePosition = normalizeImagePosition(contact.backgroundImagePosition);
  const spotlightLogo = resolveLandingAssetUrl(catalog.spotlight?.logoUrl);
  const spotlightBackground = resolveLandingAssetUrl(catalog.spotlight?.backgroundImageUrl);
  const spotlightBackgroundPosition = normalizeImagePosition(catalog.spotlight?.backgroundImagePosition);
  const categoriesBackground = resolveLandingAssetUrl(categoriesVisual.backgroundImageUrl);
  const categoriesBackgroundPosition = normalizeImagePosition(categoriesVisual.backgroundImagePosition);
  const footerCopyright = String(footer.copyright ?? "")
    .replace(/\{businessName\}/g, business.name ?? "")
    .replace(/\{year\}/g, String(new Date().getFullYear()));
  const navbarBusinessInfo = business.phone || business.email || business.address || "";
  const navbarButtons = Array.isArray(navigation.buttons)
    ? navigation.buttons
        .map((item, sourceIndex) => ({ item, sourceIndex }))
        .filter(({ item }) => String(item?.label || "").trim() && (item?.behavior === "mega-menu" || String(item?.target || "").trim()))
        .slice(0, 8)
    : [];
  const navbarShellStyle = navbarSurfaceOwner(navigation, "shellStyle", desktopLayout ? "desktop" : "mobile");
  const navbarBarStyle = navbarSurfaceOwner(navigation, "barStyle", desktopLayout ? "desktop" : "mobile");
  const navbarTopbarStyle = navbarSurfaceOwner(navigation, "topbarStyle");
  const navbarLoginStyle = navbarActionOwner(navigation, "login", desktopLayout ? "desktop" : "mobile");
  const navbarAccountStyle = navbarActionOwner(navigation, "account", desktopLayout ? "desktop" : "mobile");
  const navbarCartStyle = navbarActionOwner(navigation, "cart", desktopLayout ? "desktop" : "mobile");
  const navbarDrawerLoginStyle = navbarActionOwner(navigation, "login", "drawer");
  const navbarDrawerAccountStyle = navbarActionOwner(navigation, "account", "drawer");
  const navbarLogoSurface = navbarLogoSurfaceOwner(navigation, desktopLayout ? "desktop" : "mobile");
  const navbarDrawerLogoSurface = navbarLogoSurfaceOwner(navigation, "drawer");
  const navbarMobileOpenButtonStyle = navbarMobileOwner(navigation, "openButtonStyle");
  const navbarMobileCloseButtonStyle = navbarMobileOwner(navigation, "closeButtonStyle");
  const navbarMobileDrawerStyle = navbarMobileOwner(navigation, "drawerStyle");
  const navbarMobileDrawerHeaderStyle = navbarMobileOwner(navigation, "drawerHeaderStyle");
  const navbarAccountPreviewSelected = selectedElement?.sectionId === "navbar" && selectedNavbarPath === "navigation.accountLabel";
  const navbarCartPreviewSelected = selectedElement?.sectionId === "navbar" && selectedNavbarPath === "navigation.cartLabel";
  const navbarMobileDrawerPreviewActive = !desktopLayout && studioMode && (activeSection === "navbar" || selectedElement?.sectionId === "navbar");
  const navbarCatalogPreviewActive = selectedNavbarPath === "navigation.productsLabel" || selectedNavbarPath.startsWith("navigation.megaMenu");
  const heroHasContent = Boolean(
    heroImage ||
    hero.eyebrow ||
    heroTitle ||
    heroSubtitle ||
    (Array.isArray(hero.rotatingPhrases) && hero.rotatingPhrases.some(Boolean)) ||
    showHeroPrimaryCta ||
    showHeroSecondaryCta
  );
  const showSpotlightCta = Boolean(
    catalog.spotlight?.ctaLabel &&
    catalog.spotlight?.ctaUrl &&
    (!catalog.spotlight?.ctaRequiresProducts || productsEnabled)
  );
  const spotlightHasContent = Boolean(
    spotlightLogo ||
    catalog.spotlight?.eyebrow ||
    catalog.spotlight?.title ||
    catalog.spotlight?.description ||
    showSpotlightCta
  );
  const aboutHasCopy = Boolean(about.eyebrow || about.title || about.description || showAboutCta);
  const aboutHasContent = Boolean(aboutImage || aboutHasCopy);
  const visibleHighlights = highlights
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => item?.title || item?.description || item?.imageUrl || item?.youtubeUrl);
  const highlightsHasContent = Boolean(
    highlightsIntro.eyebrow ||
    highlightsIntro.title ||
    highlightsIntro.subtitle ||
    visibleHighlights.length
  );
  const imageCarouselItems = (Array.isArray(imageCarousel.items) ? imageCarousel.items : [])
    .map((item, index) => ({ item, index, imageUrl: resolveLandingAssetUrl(item?.imageUrl) }))
    .filter(({ item, imageUrl }) => imageUrl || item?.caption);
  const imageCarouselHasContent = Boolean(
    imageCarousel.eyebrow || imageCarousel.title || imageCarousel.subtitle || imageCarouselItems.length
  );
  const testimonialItems = (Array.isArray(testimonials.items) ? testimonials.items : [])
    .map((item, index) => ({ item, index, avatarUrl: resolveLandingAssetUrl(item?.avatarUrl) }))
    .filter(({ item, avatarUrl }) => item?.quote || item?.name || item?.role || avatarUrl);
  const testimonialsHasContent = Boolean(
    testimonials.eyebrow || testimonials.title || testimonials.subtitle || testimonialItems.length
  );
  const businessAddress = [business.address, business.city, business.province, business.country]
    .map((item) => String(item || "").trim())
    .filter(Boolean)
    .join(", ");
  const whatsappDigits = String(business.whatsapp || "").replace(/\D+/g, "");
  const contactHasDetails = Boolean(
    businessAddress || business.schedule || business.phone || business.email
  );
  const contactSocialCount = [
    business.socials?.instagram,
    business.socials?.facebook,
    business.socials?.linkedin,
    business.socials?.website,
  ].filter((value) => String(value || "").trim()).length;
  const contactHasActions = Boolean(
    (whatsappDigits && contact.whatsappCtaLabel) ||
    (contact.detailsCtaLabel && contact.detailsCtaUrl) ||
    contactHasDetails
  );
  const contactHasContent = Boolean(
    contact.title ||
    contact.subtitle ||
    contactHasActions ||
    contactImage ||
    business.mapEmbedUrl ||
    contactSocialCount
  );
  const footerBrandTagline = footer.tagline ?? business.shortDescription ?? "";
  const footerHomeLabel = footer.homeLabel ?? navigation.homeLabel ?? "Inicio";
  const footerProductsLabel = footer.productsLabel ?? navigation.productsLabel ?? "Productos";
  const footerContactLabel = footer.contactLabel ?? navigation.contactLabel ?? "Contacto";
  const footerHasBrand = Boolean((footer.showLogo !== false && logo) || business.name || footerBrandTagline);
  const footerSectionLinkVisible = (url, hasTarget) => {
    const target = String(url || "").trim();
    return !target.startsWith("#") || hasTarget;
  };
  const footerAboutVisible =
    sections.showAbout !== false &&
    navigation.showAboutLink !== false &&
    footerSectionLinkVisible(navigation.aboutUrl || "#about", aboutHasContent);
  const footerHighlightsVisible =
    sections.showHighlights !== false &&
    navigation.showHighlightsLink !== false &&
    footerSectionLinkVisible(navigation.highlightsUrl || "#highlights", highlightsHasContent);
  const footerContactVisible =
    sections.showContact !== false &&
    navigation.showContactLink !== false &&
    footerSectionLinkVisible(navigation.contactUrl || "/contact", contactHasContent);
  const footerHasNavigation = Boolean(
    footer.navigationTitle ||
    footerHomeLabel ||
    gastronomicHome ||
    (productsEnabled && navigation.showProductsLink !== false && footerProductsLabel) ||
    (footerAboutVisible && navigation.aboutLabel) ||
    (footerHighlightsVisible && navigation.highlightsLabel) ||
    (footerContactVisible && footerContactLabel)
  );
  const footerHasContact = Boolean(
    businessAddress || business.schedule || business.email || business.phone
  );
  const footerSocialLabels = navigation.showSocialLinks === false
    ? []
    : [
        whatsappDigits ? "WhatsApp" : "",
        business.socials?.instagram ? "Instagram" : "",
        business.socials?.facebook ? "Facebook" : "",
        business.socials?.youtube ? "YouTube" : "",
        business.socials?.linkedin ? "LinkedIn" : "",
        business.socials?.tiktok ? "TikTok" : "",
        business.socials?.website ? "Sitio web" : "",
      ].filter(Boolean);
  const footerHasVisibleContent = Boolean(
    footerHasBrand ||
    footerHasNavigation ||
    footerHasContact ||
    footerSocialLabels.length ||
    footerCopyright
  );
  const width = previewDevice.width;
  const scaleFor = (sectionId, kind) => segmentVisualScale(theme, sectionId, kind);
  const navbarStyle = normalizeVisualStyle(
    navigation.style,
    visualStyleFromTemplate(theme.template)
  );
  const footerStyle = normalizeVisualStyle(
    footer.style,
    visualStyleFromTemplate(theme.template)
  );
  const footerLogoPosition = normalizeImagePosition(footer.logoPosition);
  const navbarCommercial = navbarStyle === "style-2";
  const navbarSpacingScale = scaleFor("navbar", "spacing");
  const navbarImageScale = scaleFor("navbar", "image");
  const navbarTitleScale = scaleFor("navbar", "title");
  const heroSpacingScale = scaleFor("hero", "spacing");
  const heroImageScale = scaleFor("hero", "image");
  const heroTitleScale = heroTitleVisualScale(theme);
  const heroVisualStyle = segmentVisualStyleForSection("hero");
  const heroUsesLocalColors = heroVisualStyle.useGlobalColors === false;
  const heroTitleFontScale = elementFontScaleForSection("hero", heroTitleFieldPath);
  const heroSubtitleFontScale = elementFontScaleForSection("hero", heroSubtitleFieldPath);
  const heroEyebrowFontScale = elementFontScaleForSection("hero", "hero.eyebrow");
  const heroTitleSize = scaleResponsiveCss(heroTitleFontSize(heroTitle), heroTitleScale * heroTitleFontScale);
  const heroSubtitleSize = scaleResponsiveCss(heroSubtitleFontSize(heroSubtitle), heroSubtitleFontScale);
  const heroEyebrowSize = scaleResponsiveCss(heroEyebrowFontSize(typography.baseFontSize), heroEyebrowFontScale);
  const heroHasRotatingPhrases = Array.isArray(hero.rotatingPhrases) && hero.rotatingPhrases.some((phrase) => Boolean(String(phrase || "").trim()));
  const heroTitleBaseColor = heroUsesLocalColors
    ? heroColors.onSecondary
    : (heroHasRotatingPhrases ? (theme.primaryColor || "#E44E31") : "#FFFFFF");
  const heroEyebrowBaseColor = heroUsesLocalColors ? heroColors.onSecondary : "rgba(255,255,255,.78)";
  const heroSubtitleBaseColor = heroUsesLocalColors ? heroColors.onSecondary : "rgba(255,255,255,.88)";
  const spotlightSpacingScale = scaleFor("spotlight", "spacing");
  const spotlightImageScale = scaleFor("spotlight", "image");
  const spotlightTitleScale = scaleFor("spotlight", "title");
  const featuredSpacingScale = scaleFor("featured", "spacing");
  const featuredImageScale = scaleFor("featured", "image");
  const featuredTitleScale = scaleFor("featured", "title");
  const brandsSpacingScale = scaleFor("brands", "spacing");
  const brandsImageScale = scaleFor("brands", "image");
  const brandsTitleScale = scaleFor("brands", "title");
  const categoriesSpacingScale = scaleFor("categories", "spacing");
  const categoriesImageScale = scaleFor("categories", "image");
  const categoriesTitleScale = scaleFor("categories", "title");
  const aboutSpacingScale = scaleFor("about", "spacing");
  const aboutImageScale = scaleFor("about", "image");
  const aboutTitleScale = scaleFor("about", "title");
  const highlightsSpacingScale = scaleFor("highlights", "spacing");
  const highlightsImageScale = scaleFor("highlights", "image");
  const highlightsTitleScale = scaleFor("highlights", "title");
  const contactSpacingScale = scaleFor("contact", "spacing");
  const contactImageScale = scaleFor("contact", "image");
  const contactTitleScale = scaleFor("contact", "title");
  const footerSpacingScale = scaleFor("footer", "spacing");
  const footerImageScale = scaleFor("footer", "image");
  const footerTitleScale = scaleFor("footer", "title");

  const openImageEditor = (sectionId, imagePath) => (event) => {
    if (!studioMode || !onOpenSegmentTool) return;
    event?.stopPropagation?.();
    onSelectSection?.(sectionId);
    onOpenSegmentTool(sectionId, "image", imagePath || "");
  };

  const editableImageProps = (sectionId, imagePath, editLabel) => (
    studioMode && onSelectElement
      ? { landingField: imagePath, editLabel }
      : studioMode && onOpenSegmentTool
        ? { onEdit: openImageEditor(sectionId, imagePath), editLabel }
        : {}
  );

  const hiddenNavbarTarget = (fieldPath, label) => studioMode ? (
    <Button
      key={`hidden:${fieldPath}`}
      size="small"
      variant="text"
      data-landing-field={fieldPath}
      data-landing-element-type="button"
      data-landing-edit-label={`Editar ${label}`}
      sx={{
        minWidth: 0,
        px: 0.85,
        py: 0.25,
        border: "1px dashed currentColor",
        borderRadius: 999,
        opacity: 0.52,
        textTransform: "none",
        fontSize: ".72rem",
        whiteSpace: "nowrap",
      }}
    >
      {label} · oculto
    </Button>
  ) : null;


  const studioTarget = ({ fieldPath = "", elementId = "", label, count = null, muted = false }) => studioMode ? (
    <Button
      key={fieldPath || elementId}
      size="small"
      variant="text"
      data-landing-field={fieldPath || undefined}
      data-landing-element-id={elementId || undefined}
      data-landing-element-type={elementId ? "complex" : (/CtaLabel$/.test(fieldPath) ? "button" : "text")}
      data-landing-edit-label={`Editar ${label}`}
      sx={{
        minWidth: 0,
        px: 0.9,
        py: 0.35,
        border: "1px dashed rgba(255,255,255,.58)",
        borderRadius: 999,
        bgcolor: "rgba(25,36,47,.66)",
        color: "#fff",
        opacity: muted ? 0.7 : 1,
        textTransform: "none",
        fontSize: ".72rem",
        fontWeight: 850,
        backdropFilter: "blur(8px)",
        "&:hover": { bgcolor: "rgba(25,36,47,.84)" },
      }}
    >
      {label}{count === null ? "" : ` · ${count}`}
    </Button>
  ) : null;

  return (
    <Box
      data-landing-preview-device={previewDevice.id}
      data-landing-preview-width={width}
      sx={{
        width: bare ? "auto" : `${width}px`,
        maxWidth: bare ? undefined : "none",
        minHeight: "100%",
        bgcolor: background,
        color: text,
        overflow: "hidden",
        position: "relative",
        display: bare ? "contents" : "flex",
        flexDirection: bare ? undefined : "column",
        boxShadow: mobileLayout ? shadows.lg : "none",
        borderRadius: mobileLayout ? px(borders.radiusLg, 18) : 0,
        fontFamily: typography.bodyFontFamily || typography.fontFamily || "Asap, sans-serif",
        fontSize: px(typography.baseFontSize, 16),
        lineHeight: finite(typography.lineHeight, 1.5),
        "& h1, & h2, & h3, & h4, & h5, & h6": {
          fontFamily: typography.headingFontFamily || typography.fontFamily || "inherit",
          lineHeight: finite(typography.headingLineHeight, 1.15),
          letterSpacing: `${finite(typography.letterSpacing, 0)}em`,
        },
        "& .MuiButton-root": {
          borderRadius: px(borders.radiusPill, 9999),
          fontWeight: finite(typography.fontWeightMedium, 600),
        },
      }}
    >
      {segmentAllowed("navbar") && sections.showNavbar !== false ? (
        <SectionFrame
          sectionId="navbar"
          layoutOrder={orderFor("navbar")}
          activeSection={activeSection}
          studioMode={studioMode}
          onSelectSection={onSelectSection}
          onSelectElement={onSelectElement}
          selectedElement={selectedElement}
          onOpenSegmentTool={onOpenSegmentTool}
          onToggleSection={onToggleSection} onRemoveSection={onRemoveSection}
          device={previewDevice.id}
          backgroundSx={{ backgroundColor: navbarShellStyle.backgroundColor }}
          sx={{ borderColor: navbarShellStyle.borderColor, boxShadow: navbarShellStyle.boxShadow }}
        >
          {navbarCommercial && navbarBusinessInfo ? (
            <Box sx={{ px: horizontalPadding, py: 0.55 * navbarSpacingScale, bgcolor: navbarTopbarStyle.backgroundColor, color: navbarTopbarStyle.textColor, borderBottom: "1px solid", borderColor: navbarTopbarStyle.borderColor }}>
              <Typography data-landing-field="business.phone" variant="caption" sx={{ opacity: 0.82 }}>
                {navbarBusinessInfo}
              </Typography>
            </Box>
          ) : null}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
            sx={{
              px: horizontalPadding,
              py: px(finite(spacing.md, 16) * navbarSpacingScale, 16),
              bgcolor: navbarBarStyle.backgroundColor,
              color: navbarBusinessNameColor(navigation, desktopLayout ? "desktop" : "mobile"),
              borderBottom: "1px solid",
              borderColor: navbarBarStyle.borderColor,
              boxShadow: navbarBarStyle.boxShadow,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
              {navbarLogo ? (
                <Box
                  data-landing-element-id="navbar.logo"
                  data-landing-element-type="image"
                  data-landing-edit-label="Editar logo"
                  sx={{
                    display: "inline-flex",
                    minWidth: 0,
                    px: `${Number(navbarLogoSurface.paddingX || 0)}px`,
                    py: `${Number(navbarLogoSurface.paddingY || 0)}px`,
                    bgcolor: navbarLogoSurface.backgroundColor,
                    border: "1px solid",
                    borderColor: navbarLogoSurface.borderColor,
                    borderRadius: `${Number(navbarLogoSurface.borderRadius || 0)}px`,
                    boxShadow: navbarLogoSurface.boxShadow,
                  }}
                >
                  <SafeCanvasImage
                    src={navbarLogo}
                    alt=""
                    monochromeColor={navbarLogoColor}
                    sx={navbarLogoMaxHeight !== null
                      ? {
                          display: "block",
                          width: "auto",
                          maxWidth: desktopLayout ? 220 : "min(42vw, 150px)",
                          height: navbarLogoMaxHeight,
                          maxHeight: navbarLogoMaxHeight,
                          objectFit: "contain",
                        }
                      : { width: 38 * navbarImageScale, height: 38 * navbarImageScale, objectFit: "contain" }}
                  />
                </Box>
              ) : navigation.showLogo === false && studioMode ? (
                <Button
                  size="small"
                  variant="outlined"
                  data-landing-element-id="navbar.logo"
                  data-landing-element-type="image"
                  data-landing-edit-label="Editar logo"
                  sx={{ textTransform: "none", borderStyle: "dashed", opacity: 0.72 }}
                >
                  Logo oculto
                </Button>
              ) : business.name ? (
                <Stack direction="row" spacing={0.7} alignItems="center" sx={{ minWidth: 0 }}>
                  <Typography
                    data-landing-field="business.name"
                    data-landing-edit-label="Editar nombre del negocio"
                    noWrap
                    sx={{
                      fontSize: `${finite(typography.baseFontSize, 16) * navbarTitleScale}px`,
                      fontWeight: "var(--wp-segment-title-weight)",
                      letterSpacing: "var(--wp-segment-letter-spacing)",
                      color: navbarBusinessNameColor(navigation, desktopLayout ? "desktop" : "mobile"),
                    }}
                  >
                    {business.name}
                  </Typography>
                  {studioMode && navigation.showLogo !== false ? (
                    <Button
                      size="small"
                      data-landing-element-id="navbar.logo"
                      data-landing-element-type="image"
                      data-landing-edit-label="Agregar logo"
                      sx={{ minWidth: 0, px: 0.8, textTransform: "none", fontSize: ".72rem" }}
                    >
                      + Logo
                    </Button>
                  ) : null}
                </Stack>
              ) : studioMode ? (
                <Button
                  size="small"
                  data-landing-element-id="navbar.logo"
                  data-landing-element-type="image"
                  data-landing-edit-label="Agregar logo"
                  sx={{ textTransform: "none", borderStyle: "dashed" }}
                >
                  Agregar logo
                </Button>
              ) : null}
            </Stack>
            <Stack direction="row" spacing={0.2} alignItems="center" sx={{ display: desktopLayout ? "flex" : "none", minWidth: 0 }}>
              {navigation.homeLabel ? (
                <Button
                  data-landing-field="navigation.homeLabel"
                  data-landing-element-type="button"
                  data-landing-edit-label="Editar Inicio"
                  sx={navbarCanvasDesktopLinkSx(navbarLinkOwner(navigation, "home", "desktop"), true)}
                >
                  <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{navigation.homeLabel}</Box>
                </Button>
              ) : hiddenNavbarTarget("navigation.homeLabel", "Inicio")}

              {gastronomicMenuEnabled ? (
                <Button
                  sx={navbarCanvasDesktopLinkSx(navbarLinkOwner(navigation, "catalog", "desktop"), navbarCatalogPreviewActive)}
                >
                  Carta
                </Button>
              ) : null}

              {productsEnabled && navigation.showProductsLink !== false && navigation.productsLabel ? (
                <Button
                  data-landing-field="navigation.productsLabel"
                  data-landing-element-type="button"
                  data-landing-edit-label="Editar Productos o servicios"
                  endIcon={<KeyboardArrowDownRoundedIcon sx={{ fontSize: 20, transform: navbarCatalogPreviewActive ? "rotate(180deg)" : "none", transition: "transform .2s ease" }} />}
                  sx={navbarCanvasDesktopLinkSx(navbarLinkOwner(navigation, "catalog", "desktop"), navbarCatalogPreviewActive)}
                >
                  <Box component="span" sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{navigation.productsLabel}</Box>
                </Button>
              ) : productsEnabled ? hiddenNavbarTarget("navigation.productsLabel", "Productos") : null}

              {footerAboutVisible && navigation.aboutLabel ? (
                <Button
                  data-landing-field="navigation.aboutLabel"
                  data-landing-element-type="button"
                  data-landing-edit-label="Editar Sobre nosotros"
                  sx={navbarCanvasDesktopLinkSx(navbarLinkOwner(navigation, "about", "desktop"), false)}
                >
                  <Box component="span" sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{navigation.aboutLabel}</Box>
                </Button>
              ) : (navigation.showAboutLink === false || !navigation.aboutLabel) ? hiddenNavbarTarget("navigation.aboutLabel", "Sobre nosotros") : null}

              {footerHighlightsVisible && navigation.highlightsLabel ? (
                <Button
                  data-landing-field="navigation.highlightsLabel"
                  data-landing-element-type="button"
                  data-landing-edit-label="Editar Diferenciales"
                  sx={navbarCanvasDesktopLinkSx(navbarLinkOwner(navigation, "highlights", "desktop"), false)}
                >
                  <Box component="span" sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{navigation.highlightsLabel}</Box>
                </Button>
              ) : (navigation.showHighlightsLink === false || !navigation.highlightsLabel) ? hiddenNavbarTarget("navigation.highlightsLabel", "Diferenciales") : null}

              {footerContactVisible && navigation.contactLabel ? (
                <Button
                  data-landing-field="navigation.contactLabel"
                  data-landing-element-type="button"
                  data-landing-edit-label="Editar Contacto"
                  sx={navbarCanvasDesktopLinkSx(navbarLinkOwner(navigation, "contact", "desktop"), false)}
                >
                  <Box component="span" sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{navigation.contactLabel}</Box>
                </Button>
              ) : (navigation.showContactLink === false || !navigation.contactLabel) ? hiddenNavbarTarget("navigation.contactLabel", "Contacto") : null}

              {navbarButtons.map(({ item, sourceIndex }) => {
                const buttonStyle = navbarCustomButtonOwner(item, "desktop");
                const active = item?.behavior === "mega-menu" && megaMenuPreviewActive && selectedNavbarButton?.id === item?.id;
                return (
                  <Button
                    data-navbar-button-id={item?.id || undefined}
                    data-landing-field={`navigation.buttons.${sourceIndex}.label`}
                    data-landing-element-type="button"
                    data-landing-edit-label={`Editar botón ${sourceIndex + 1}`}
                    key={item?.id || `navbar-button-${sourceIndex}`}
                    endIcon={item?.behavior === "mega-menu" ? <KeyboardArrowDownRoundedIcon sx={{ fontSize: 20 }} /> : undefined}
                    sx={navbarCanvasDesktopLinkSx(buttonStyle, active)}
                  >
                    <Box component="span" sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.label}</Box>
                  </Button>
                );
              })}

              {studioMode ? (
                <Button
                  size="small"
                  data-landing-element-id="navbar.buttons-manager"
                  data-landing-element-type="complex"
                  data-landing-edit-label="Administrar botones"
                  sx={{ minWidth: 0, px: 0.9, textTransform: "none", fontSize: ".72rem", opacity: 0.72 }}
                >
                  Administrar botones
                </Button>
              ) : null}

              {navigation.showCustomerAccess !== false ? (
                navbarAccountPreviewSelected ? (
                  <IconButton
                    data-landing-field="navigation.accountLabel"
                    data-landing-element-type="button"
                    data-landing-edit-label="Editar cuenta"
                    aria-label={navigation.accountLabel || "Mi cuenta"}
                    sx={navbarCanvasActionSx(navbarAccountStyle, false)}
                  >
                    <Avatar
                      sx={{
                        width: 31,
                        height: 31,
                        bgcolor: navbarAccountStyle.avatarBackgroundColor,
                        color: navbarAccountStyle.avatarTextColor,
                        fontSize: ".86rem",
                        fontWeight: 900,
                      }}
                    >
                      U
                    </Avatar>
                  </IconButton>
                ) : navigation.loginLabel ? (
                  <Button
                    data-landing-field="navigation.loginLabel"
                    data-landing-element-type="button"
                    data-landing-edit-label="Editar acceso de clientes"
                    sx={navbarCanvasLoginSx(navbarLoginStyle)}
                  >
                    {navigation.loginLabel}
                  </Button>
                ) : hiddenNavbarTarget("navigation.loginLabel", "Acceso clientes")
              ) : hiddenNavbarTarget("navigation.loginLabel", "Acceso clientes")}

              {navigation.showCart !== false && productsEnabled ? (
                <IconButton
                  data-landing-field="navigation.cartLabel"
                  data-landing-element-type="button"
                  data-landing-edit-label="Editar carrito"
                  aria-label={navigation.cartAriaLabel || "Abrir carrito"}
                  sx={navbarCanvasActionSx(navbarCartStyle, false)}
                >
                  <Badge
                    badgeContent={navbarCartPreviewSelected ? 1 : 0}
                    max={99}
                    sx={{
                      "& .MuiBadge-badge": {
                        minWidth: 18,
                        height: 18,
                        px: 0.45,
                        fontSize: ".68rem",
                        fontWeight: 900,
                        bgcolor: navbarCartStyle.badgeBackgroundColor,
                        color: navbarCartStyle.badgeTextColor,
                      },
                    }}
                  >
                    <ShoppingCartOutlinedIcon sx={{ fontSize: 22 }} />
                  </Badge>
                </IconButton>
              ) : null}
            </Stack>
            {!desktopLayout ? (
              <Stack direction="row" spacing={0.75} alignItems="center">
                {navigation.showCustomerAccess !== false && navigation.loginLabel ? (
                  navbarAccountPreviewSelected ? (
                    <IconButton
                      data-landing-field="navigation.accountLabel"
                      data-landing-element-type="button"
                      data-landing-edit-label="Editar cuenta"
                      aria-label={navigation.accountLabel || "Mi cuenta"}
                      sx={navbarCanvasActionSx(navbarAccountStyle, true)}
                    >
                      <Avatar
                        sx={{
                          width: 31,
                          height: 31,
                          bgcolor: navbarAccountStyle.avatarBackgroundColor,
                          color: navbarAccountStyle.avatarTextColor,
                          fontSize: ".86rem",
                          fontWeight: 900,
                        }}
                      >
                        U
                      </Avatar>
                    </IconButton>
                  ) : (
                    <IconButton
                      data-landing-field="navigation.loginLabel"
                      data-landing-element-type="button"
                      data-landing-edit-label="Editar acceso de clientes"
                      aria-label={navigation.loginLabel}
                      sx={navbarCanvasActionSx(navbarLoginStyle, true)}
                    >
                      <PersonOutlineRoundedIcon />
                    </IconButton>
                  )
                ) : null}

                {navigation.showCart !== false && productsEnabled ? (
                  <IconButton
                    data-landing-field="navigation.cartLabel"
                    data-landing-element-type="button"
                    data-landing-edit-label="Editar carrito"
                    aria-label={navigation.cartAriaLabel || "Abrir carrito"}
                    sx={navbarCanvasActionSx(navbarCartStyle, true)}
                  >
                    <Badge
                      badgeContent={navbarCartPreviewSelected ? 1 : 0}
                      max={99}
                      sx={{
                        "& .MuiBadge-badge": {
                          minWidth: 18,
                          height: 18,
                          px: 0.45,
                          fontSize: ".68rem",
                          fontWeight: 900,
                          bgcolor: navbarCartStyle.badgeBackgroundColor,
                          color: navbarCartStyle.badgeTextColor,
                        },
                      }}
                    >
                      <ShoppingCartOutlinedIcon sx={{ fontSize: 22 }} />
                    </Badge>
                  </IconButton>
                ) : null}

                <IconButton
                  size="small"
                  data-landing-element-id="navbar.mobile-menu"
                  data-landing-element-type="complex"
                  data-landing-edit-label="Editar menú móvil"
                  aria-label={navigation.menuAriaLabel || "Abrir menú"}
                  onClick={studioMode && !onSelectElement ? (event) => {
                    event.stopPropagation();
                    onSelectSection?.("navbar");
                  } : undefined}
                  sx={{
                    flexShrink: 0,
                    width: navbarMobileOpenButtonStyle.size,
                    height: navbarMobileOpenButtonStyle.size,
                    color: navbarMobileOpenButtonStyle.color,
                    border: `1px solid ${navbarMobileOpenButtonStyle.borderColor}`,
                    bgcolor: navbarMobileOpenButtonStyle.backgroundColor,
                    "&:hover": {
                      color: navbarMobileOpenButtonStyle.hoverColor,
                      bgcolor: navbarMobileOpenButtonStyle.hoverBackgroundColor,
                      borderColor: navbarMobileOpenButtonStyle.hoverBorderColor,
                    },
                    "&:focus-visible": {
                      outline: `3px solid ${navbarMobileOpenButtonStyle.focusColor}`,
                      outlineOffset: 2,
                    },
                  }}
                >
                  <MenuRoundedIcon fontSize="small" />
                </IconButton>
              </Stack>
            ) : null}
          </Stack>
          {navbarMobileDrawerPreviewActive ? (
            <Box
              data-landing-element-id="navbar.mobile-drawer-preview"
              sx={{
                width: "100%",
                bgcolor: navbarMobileDrawerStyle.backgroundColor,
                borderTop: "1px solid",
                borderColor: navbarMobileDrawerStyle.borderColor,
                boxShadow: navbarMobileDrawerStyle.boxShadow,
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={2}
                sx={{
                  p: 1.5,
                  bgcolor: navbarMobileDrawerHeaderStyle.backgroundColor,
                  borderBottom: "1px solid",
                  borderColor: navbarMobileDrawerHeaderStyle.borderColor,
                  boxShadow: navbarMobileDrawerHeaderStyle.boxShadow,
                }}
              >
                <Stack direction="row" spacing={0.8} alignItems="center" sx={{ minWidth: 0 }}>
                  {navbarLogo ? (
                    <Box
                      sx={{
                        display: "inline-flex",
                        minWidth: 0,
                        px: `${Number(navbarDrawerLogoSurface.paddingX || 0)}px`,
                        py: `${Number(navbarDrawerLogoSurface.paddingY || 0)}px`,
                        bgcolor: navbarDrawerLogoSurface.backgroundColor,
                        border: "1px solid",
                        borderColor: navbarDrawerLogoSurface.borderColor,
                        borderRadius: `${Number(navbarDrawerLogoSurface.borderRadius || 0)}px`,
                        boxShadow: navbarDrawerLogoSurface.boxShadow,
                      }}
                    >
                      <SafeCanvasImage
                        src={navbarLogo}
                        alt=""
                        monochromeColor={navbarLogoColor}
                        sx={{ width: "auto", maxWidth: 140, height: navbarLogoMaxHeight || 34, maxHeight: navbarLogoMaxHeight || 34, objectFit: "contain" }}
                      />
                    </Box>
                  ) : business.name ? (
                    <Typography
                      data-landing-field="business.name"
                      noWrap
                      sx={{ fontWeight: 900, color: navbarBusinessNameColor(navigation, "drawer") }}
                    >
                      {business.name}
                    </Typography>
                  ) : null}
                </Stack>
                <IconButton
                  data-landing-element-id="navbar.mobile-menu"
                  data-landing-element-type="complex"
                  data-landing-edit-label="Editar menú móvil"
                  aria-label={navigation.closeMenuAriaLabel || "Cerrar menú"}
                  sx={{
                    width: navbarMobileCloseButtonStyle.size,
                    height: navbarMobileCloseButtonStyle.size,
                    color: navbarMobileCloseButtonStyle.color,
                    bgcolor: navbarMobileCloseButtonStyle.backgroundColor,
                    border: "1px solid",
                    borderColor: navbarMobileCloseButtonStyle.borderColor,
                    "&:hover": {
                      color: navbarMobileCloseButtonStyle.hoverColor,
                      bgcolor: navbarMobileCloseButtonStyle.hoverBackgroundColor,
                      borderColor: navbarMobileCloseButtonStyle.hoverBorderColor,
                    },
                    "&:focus-visible": {
                      outline: `3px solid ${navbarMobileCloseButtonStyle.focusColor}`,
                      outlineOffset: 2,
                    },
                  }}
                >
                  <CloseRoundedIcon />
                </IconButton>
              </Stack>

              <Box sx={{ p: 1.5 }}>
                <Stack spacing={`${Number(navigation.mobile?.itemGap ?? 5.2)}px`}>
                  <NavbarCanvasMobileItem
                    style={navbarLinkOwner(navigation, "home", "mobile")}
                    active
                    label={navigation.homeLabel}
                    fieldPath="navigation.homeLabel"
                    endIcon={<ChevronRightRoundedIcon />}
                  />
                  {gastronomicMenuEnabled && navigation.showProductsLink !== false ? (
                    <NavbarCanvasMobileItem
                      style={navbarLinkOwner(navigation, "catalog", "mobile")}
                      active={navbarCatalogPreviewActive}
                      label="Carta"
                      fieldPath="navigation.productsLabel"
                      endIcon={<ChevronRightRoundedIcon />}
                    />
                  ) : productsEnabled && navigation.showProductsLink !== false ? (
                    <NavbarCanvasMobileItem
                      style={navbarLinkOwner(navigation, "catalog", "mobile")}
                      active={navbarCatalogPreviewActive}
                      label={navigation.productsLabel}
                      fieldPath="navigation.productsLabel"
                      endIcon={<KeyboardArrowDownRoundedIcon />}
                    />
                  ) : null}
                  {footerAboutVisible && navigation.showAboutLink !== false ? (
                    <NavbarCanvasMobileItem
                      style={navbarLinkOwner(navigation, "about", "mobile")}
                      label={navigation.aboutLabel}
                      fieldPath="navigation.aboutLabel"
                      endIcon={<ChevronRightRoundedIcon />}
                    />
                  ) : null}
                  {footerHighlightsVisible && navigation.showHighlightsLink !== false ? (
                    <NavbarCanvasMobileItem
                      style={navbarLinkOwner(navigation, "highlights", "mobile")}
                      label={navigation.highlightsLabel}
                      fieldPath="navigation.highlightsLabel"
                      endIcon={<ChevronRightRoundedIcon />}
                    />
                  ) : null}
                  {footerContactVisible && navigation.showContactLink !== false ? (
                    <NavbarCanvasMobileItem
                      style={navbarLinkOwner(navigation, "contact", "mobile")}
                      label={navigation.contactLabel}
                      fieldPath="navigation.contactLabel"
                      endIcon={<ChevronRightRoundedIcon />}
                    />
                  ) : null}
                  {navbarButtons.map(({ item, sourceIndex }) => (
                    <NavbarCanvasMobileItem
                      key={item?.id || `navbar-mobile-button-${sourceIndex}`}
                      style={navbarCustomButtonOwner(item, "mobile")}
                      active={item?.behavior === "mega-menu" && megaMenuPreviewActive && selectedNavbarButton?.id === item?.id}
                      label={item.label}
                      fieldPath={`navigation.buttons.${sourceIndex}.label`}
                      buttonId={item?.id || ""}
                      endIcon={item?.behavior === "mega-menu" ? <KeyboardArrowDownRoundedIcon /> : <ChevronRightRoundedIcon />}
                    />
                  ))}
                </Stack>

                {navigation.showCustomerAccess !== false ? (
                  <Box sx={{ mt: 1.4 }}>
                    {navbarAccountPreviewSelected ? (
                      <Box
                        data-landing-field="navigation.accountLabel"
                        sx={{
                          p: 1.7,
                          borderRadius: 2.5,
                          bgcolor: navbarDrawerAccountStyle.containerBackgroundColor,
                          color: navbarDrawerAccountStyle.primaryTextColor,
                          border: "1px solid",
                          borderColor: navbarDrawerAccountStyle.containerBorderColor,
                        }}
                      >
                        <Stack direction="row" spacing={1.1} alignItems="center">
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              display: "grid",
                              placeItems: "center",
                              borderRadius: "50%",
                              bgcolor: navbarDrawerAccountStyle.avatarBackgroundColor,
                              color: navbarDrawerAccountStyle.avatarTextColor,
                              fontWeight: 900,
                            }}
                          >
                            U
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 900 }} noWrap>{navigation.accountLabel || "Mi cuenta"}</Typography>
                            <Typography variant="caption" sx={{ color: navbarDrawerAccountStyle.secondaryTextColor }}>usuario@ejemplo.com</Typography>
                          </Box>
                        </Stack>
                      </Box>
                    ) : navigation.loginLabel ? (
                      <Button
                        data-landing-field="navigation.loginLabel"
                        fullWidth
                        variant="contained"
                        startIcon={<PersonOutlineRoundedIcon />}
                        sx={{
                          minHeight: 46,
                          borderRadius: 1.8,
                          color: navbarDrawerLoginStyle.color,
                          bgcolor: navbarDrawerLoginStyle.backgroundColor,
                          borderColor: navbarDrawerLoginStyle.borderColor,
                          textTransform: "none",
                          "&:hover": {
                            color: navbarDrawerLoginStyle.hoverColor,
                            bgcolor: navbarDrawerLoginStyle.hoverBackgroundColor,
                            borderColor: navbarDrawerLoginStyle.hoverBorderColor,
                          },
                        }}
                      >
                        {navigation.loginLabel}
                      </Button>
                    ) : null}
                  </Box>
                ) : null}
              </Box>
            </Box>
          ) : null}
          {megaMenuPreviewActive ? (
            <Box
              data-landing-element-id="navbar.mega-menu-preview"
              data-mega-menu-style={megaMenuPreviewVariant}
              sx={{
                mx: { md: 2, lg: 3 },
                mb: 2,
                p: megaMenuPreviewVariant === "catalog" ? 0 : 2.2,
                bgcolor: megaMenuVisual.surface.backgroundColor,
                border: Number(megaMenuVisual.surface.borderWidth ?? 1) > 0 ? `${Number(megaMenuVisual.surface.borderWidth ?? 1)}px solid` : "none",
                borderColor: megaMenuVisual.surface.borderColor,
                borderRadius: `0 0 ${Number(megaMenuVisual.surface.borderRadius || 22)}px ${Number(megaMenuVisual.surface.borderRadius || 22)}px`,
                boxShadow: megaMenuVisual.surface.boxShadow,
                overflow: "hidden",
              }}
            >
              {megaMenuPreviewState === "content" ? (
                <Stack
                  direction={megaMenuPreviewVariant === "catalog" ? "row" : "column"}
                  spacing={megaMenuPreviewVariant === "catalog" ? 0 : 1.4}
                  alignItems="stretch"
                >
                  {navigation.showMegaMenuIntro !== false ? (
                    <Box
                      sx={{
                        width: megaMenuPreviewVariant === "catalog" ? "30%" : "100%",
                        minWidth: megaMenuPreviewVariant === "catalog" ? 180 : 0,
                        p: megaMenuPreviewVariant === "catalog" ? 1.6 : 0,
                        bgcolor: megaMenuPreviewVariant === "catalog" ? megaMenuVisual.introSurface.backgroundColor : "transparent",
                        border: megaMenuPreviewVariant === "catalog" ? "1px solid" : "none",
                        borderColor: megaMenuVisual.introSurface.borderColor,
                        borderRadius: megaMenuPreviewVariant === "catalog" ? 2 : 0,
                      }}
                    >
                      <Typography variant="overline" sx={{ color: megaMenuPreviewVariant === "catalog" ? megaMenuVisual.eyebrow.introColor : megaMenuVisual.eyebrow.color, fontWeight: 900 }}>{navigation.megaMenuEyebrow || "COLECCIÓN"}</Typography>
                      <Typography sx={{ color: megaMenuPreviewVariant === "catalog" ? megaMenuVisual.title.introColor : megaMenuVisual.title.color, fontWeight: 950 }}>{navigation.megaMenuTitle || "Explorá el catálogo"}</Typography>
                      <Typography variant="caption" sx={{ color: megaMenuPreviewVariant === "catalog" ? megaMenuVisual.description.introColor : megaMenuVisual.description.color, display: "block", mt: 0.4 }}>{navigation.megaMenuDescription || "Vista previa del bloque destacado."}</Typography>
                      <Box sx={{ mt: 1.2, display: "inline-flex", px: 1, py: 0.45, borderRadius: 999, color: megaMenuPreviewVariant === "catalog" ? megaMenuVisual.introAllItemsButton.textColor : megaMenuVisual.allItemsButton.textColor, bgcolor: megaMenuPreviewVariant === "catalog" ? megaMenuVisual.introAllItemsButton.backgroundColor : megaMenuVisual.allItemsButton.backgroundColor, border: "1px solid", borderColor: megaMenuPreviewVariant === "catalog" ? megaMenuVisual.introAllItemsButton.borderColor : megaMenuVisual.allItemsButton.borderColor, fontSize: ".72rem", fontWeight: 850 }}>{navigation.allProductsLabel || "Ver todos"}</Box>
                    </Box>
                  ) : null}
                  <Box sx={{ flex: 1, minWidth: 0, p: megaMenuPreviewVariant === "catalog" ? 1.8 : 0 }}>
                    <Typography sx={{ color: megaMenuVisual.categoriesTitle.color, fontWeight: 950 }}>{navigation.productsLabel || "Productos"}</Typography>
                    <Typography variant="caption" sx={{ color: megaMenuVisual.categoriesSubtitle.color }}>{navigation.megaMenuCategoriesSubtitle || "Categorías destacadas"}</Typography>
                    {megaMenuPreviewVariant === "catalog" ? (
                      <Box sx={{ display: "grid", gridTemplateColumns: `repeat(${megaMenuPreviewColumns},minmax(0,1fr))`, gap: 0.7, mt: 1 }}>
                        {["Categoría principal", "Otra categoría"].map((label) => (
                          <Box key={label} sx={{ p: 0.9, borderRadius: 1.6, bgcolor: megaMenuVisual.categoryRow.backgroundColor, border: "1px solid", borderColor: megaMenuVisual.categoryRow.borderColor }}>
                            <Typography variant="body2" sx={{ color: megaMenuVisual.categoryRow.labelColor, fontWeight: 850 }}>{label}</Typography>
                            <Typography variant="caption" sx={{ color: megaMenuVisual.categoryRow.countColor }}>6 productos</Typography>
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Box sx={{ display: "grid", gridTemplateColumns: `repeat(${navigation.megaMenuLayout === "stacked" ? 1 : (megaMenuPreviewVariant === "showcase" ? 3 : megaMenuPreviewColumns)},minmax(0,1fr))`, gap: 0.8, mt: 1 }}>
                        {["Producto destacado", "Otra opción", "Colección"].slice(0, megaMenuPreviewVariant === "showcase" ? 3 : 2).map((label) => (
                          <Box key={label} sx={{ p: 1, borderRadius: 1.6, bgcolor: megaMenuVisual.previewCard.backgroundColor, border: "1px solid", borderColor: megaMenuVisual.previewCard.borderColor, boxShadow: megaMenuVisual.previewCard.boxShadow }}>
                            <Box sx={{ height: 34, borderRadius: 1, bgcolor: megaMenuVisual.previewCard.imageBackgroundColor, mb: 0.7 }} />
                            <Typography variant="caption" sx={{ color: megaMenuVisual.previewCard.categoryTextColor }}>Categoría</Typography>
                            <Typography variant="body2" sx={{ color: megaMenuVisual.previewCard.titleTextColor, fontWeight: 850 }}>{label}</Typography>
                            <Typography variant="caption" sx={{ color: megaMenuVisual.previewCard.descriptionTextColor }}>Descripción de muestra</Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                    <Stack direction="row" spacing={0.6} sx={{ mt: 1 }}>
                      <Box sx={{ px: 1, py: 0.35, borderRadius: 999, color: megaMenuVisual.categoryPill.textColor, bgcolor: megaMenuVisual.categoryPill.backgroundColor, border: "1px solid", borderColor: megaMenuVisual.categoryPill.borderColor, fontSize: ".7rem" }}>Subcategoría</Box>
                      <Box sx={{ px: 1, py: 0.35, borderRadius: 999, color: megaMenuVisual.latestItem.textColor, bgcolor: megaMenuVisual.latestItem.backgroundColor, border: "1px solid", borderColor: megaMenuVisual.latestItem.borderColor, fontSize: ".7rem" }}>Último agregado</Box>
                    </Stack>
                  </Box>
                </Stack>
              ) : null}
              <Stack direction="row" spacing={0.7} sx={{ m: megaMenuPreviewVariant === "catalog" ? 1.4 : 0, mt: megaMenuPreviewState === "content" ? 1.4 : (megaMenuPreviewVariant === "catalog" ? 1.4 : 0), pt: megaMenuPreviewState === "content" ? 1.1 : 0, borderTop: megaMenuPreviewState === "content" ? "1px solid" : "none", borderColor: megaMenuVisual.divider.color }}>
                {(megaMenuPreviewState === "content" || megaMenuPreviewState === "loading") ? <Box sx={{ flex: 1, p: 0.8, borderRadius: 1.5, color: megaMenuVisual.loading.textColor, border: "1px solid", borderColor: megaMenuVisual.loading.spinnerColor, fontSize: ".7rem" }}>● {navigation.loadingCategoriesText || "Cargando categorías…"}</Box> : null}
                {(megaMenuPreviewState === "content" || megaMenuPreviewState === "empty") ? <Box sx={{ flex: 1, p: 0.8, borderRadius: 1.5, color: megaMenuVisual.empty.textColor, bgcolor: megaMenuVisual.empty.backgroundColor, border: "1px solid", borderColor: megaMenuVisual.empty.borderColor, fontSize: ".7rem" }}>{navigation.emptyCategoriesText || "Sin categorías"}</Box> : null}
                {(megaMenuPreviewState === "content" || megaMenuPreviewState === "error") ? <Box sx={{ flex: 1, p: 0.8, borderRadius: 1.5, color: megaMenuVisual.error.textColor, bgcolor: megaMenuVisual.error.backgroundColor, border: "1px solid", borderColor: megaMenuVisual.error.borderColor, fontSize: ".7rem" }}>● {navigation.categoriesErrorText || "Error al cargar"}</Box> : null}
              </Stack>
            </Box>
          ) : null}
        </SectionFrame>
      ) : null}

      {segmentAllowed("hero") && sections.showHero !== false && (heroHasContent || studioMode) ? (
        <SectionFrame
          sectionId={layoutInstanceId || "hero"}
          sectionType="hero"
          layoutOrder={orderFor("hero")}
          activeSection={activeSection}
          studioMode={studioMode}
          onSelectSection={onSelectSection}
          onSelectElement={onSelectElement}
          selectedElement={selectedElement}
          onOpenSegmentTool={onOpenSegmentTool}
          onToggleSection={onToggleSection} onRemoveSection={onRemoveSection}
          device={previewDevice.id}
          backgroundSx={sectionBackgroundSx("hero")}
          sx={{
            ...segmentCssVariables(theme, "hero"),
            ...elementColorSxForSection("hero"),
            minHeight: `${heroPreviewMinHeight}px`,
            display: "flex",
            alignItems: heroContentAlign,
            justifyContent: heroContentJustify,
            position: "relative",
            overflow: "hidden",
            bgcolor: heroColors.secondary,
            background: `linear-gradient(135deg, ${heroColors.secondary}, ${heroColors.primary})`,
          }}
        >
          {studioMode ? (
            <Stack
              direction="row"
              spacing={0.6}
              useFlexGap
              flexWrap="wrap"
              sx={{ position: "absolute", top: 10, right: 10, zIndex: 8, justifyContent: "flex-end", maxWidth: "72%" }}
            >
              {studioTarget({ elementId: "hero.slides-manager", label: "Slider", count: heroSlideCount, muted: heroSlideCount === 0 })}
              {studioTarget({ elementId: "hero.rotating-phrases", label: "Frases", count: heroPhraseCount, muted: heroPhraseCount === 0 })}
              {!hero.eyebrow ? studioTarget({ fieldPath: "hero.eyebrow", label: "Texto superior", muted: true }) : null}
              {!heroTitle ? studioTarget({ fieldPath: heroTitleFieldPath, label: "Título", muted: true }) : null}
              {!heroSubtitle ? studioTarget({ fieldPath: heroSubtitleFieldPath, label: "Subtítulo", muted: true }) : null}
              {!heroImage ? studioTarget({ fieldPath: "hero.imageUrl", label: "Imagen", muted: true }) : null}
              {!showHeroPrimaryCta ? studioTarget({ fieldPath: "hero.primaryCtaLabel", label: "Botón principal", muted: true }) : null}
              {!showHeroSecondaryCta ? studioTarget({ fieldPath: "hero.secondaryCtaLabel", label: "Botón secundario", muted: true }) : null}
            </Stack>
          ) : null}
          {heroImage ? (
            <SafeCanvasImage
              src={heroImage}
              alt={heroSlide?.alt || heroTitle || business.name || ""}
              sx={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: heroImagePosition,
                transform: `scale(${heroImageScale})`,
                transformOrigin: "center",
              }}
              {...editableImageProps("hero", heroImageSourcePath, "Editar imagen de portada")}
            />
          ) : null}
          {hero.overlay ? (
            <Box
              aria-hidden
              sx={{
                position: "absolute",
                inset: 0,
                background: hero.overlay,
                pointerEvents: "none",
              }}
            />
          ) : null}
          <Box
            sx={{
              position: "relative",
              zIndex: 1,
              px: responsive(
                { xs: px(spacing.lg, 24), sm: px(spacing.xxl, 48), md: px(spacing.section, 72) },
                px(spacing.lg, 24)
              ),
              py: responsive(
                {
                  xs: px(finite(spacing.xl, 32) * heroSpacingScale, 32),
                  md: px(finite(spacing.section, 72) * heroSpacingScale, 72),
                },
                px(finite(spacing.xl, 32) * heroSpacingScale, 32)
              ),
              width: "100%",
              maxWidth: 780,
              textAlign: heroTextAlign,
              color: heroColors.onSecondary,
            }}
          >
            {hero.eyebrow ? <Typography data-landing-field="hero.eyebrow" data-landing-edit-label="Editar texto superior" variant="overline" sx={{ color: heroEyebrowBaseColor, fontSize: responsive(heroEyebrowSize, heroEyebrowSize.xs), fontWeight: 900, overflowWrap: "anywhere", wordBreak: "break-word", hyphens: "auto" }}>{hero.eyebrow}</Typography> : null}
            {heroTitle ? (
              <Typography
                data-landing-field={heroTitleFieldPath}
                data-landing-edit-label="Editar título principal"
                sx={{
                  mt: hero.eyebrow ? .4 : 0,
                  fontFamily: typography.headingFontFamily || "inherit",
                  fontSize: responsive(heroTitleSize, heroTitleSize.xs),
                  lineHeight: responsive({ xs: 1.02, sm: 1.04, md: 1.05 }, 1.02),
                  fontWeight: "var(--wp-segment-title-weight)",
                  letterSpacing: "var(--wp-segment-letter-spacing)",
                  color: heroTitleBaseColor,
                  overflowWrap: "normal",
                  wordBreak: "normal",
                  hyphens: "none",
                }}
              >
                {heroTitle}
              </Typography>
            ) : null}
            {heroRotatingPhrase ? (
              <Typography
                data-landing-element-id="hero.rotating-phrases"
                data-landing-element-type="complex"
                data-landing-edit-label="Editar frases rotativas"
                sx={{
                  mt: 0.8,
                  fontFamily: typography.headingFontFamily || "inherit",
                  fontWeight: 850,
                  fontSize: responsive({ xs: "1.1rem", md: "1.45rem" }, "1.1rem"),
                  color: heroColors.primary,
                  textShadow: "0 2px 12px rgba(0,0,0,.32)",
                }}
              >
                {heroRotatingPhrase}
              </Typography>
            ) : null}
            {heroSubtitle ? <Typography data-landing-field={heroSubtitleFieldPath} data-landing-edit-label="Editar texto de apoyo" sx={{ mt: heroTitle || hero.eyebrow ? 1.5 : 0, maxWidth: heroTextLengthSafe(heroSubtitle) > 420 ? 880 : 760, ml: heroHorizontalPosition === "right" ? "auto" : 0, mr: heroHorizontalPosition === "left" ? "auto" : 0, color: heroSubtitleBaseColor, fontSize: responsive(heroSubtitleSize, heroSubtitleSize.xs), lineHeight: heroTextLengthSafe(heroSubtitle) > 420 ? 1.55 : 1.6, overflowWrap: "anywhere", wordBreak: "break-word", hyphens: "auto", whiteSpace: "pre-line" }}>{heroSubtitle}</Typography> : null}
            {showHeroPrimaryCta || showHeroSecondaryCta ? (
              <Stack direction={responsive({ xs: "column", sm: "row" }, "column")} spacing={1.2} sx={{ mt: heroTitle || heroSubtitle || hero.eyebrow ? 2.4 : 0, alignItems: responsive({ xs: "stretch", sm: "center" }, "stretch"), justifyContent: responsive({ xs: "stretch", sm: heroContentJustify }, "stretch") }}>
                {showHeroPrimaryCta ? (
                  <Button
                    data-landing-field="hero.primaryCtaLabel"
                    data-landing-edit-label="Editar botón principal"
                    variant="contained"
                    startIcon={hero.primaryCtaShowIcon !== false ? <HeroCanvasCtaIcon name={hero.primaryCtaIcon} fallback="explore" /> : undefined}
                    sx={heroCtaCanvasSx(hero.primaryCtaStyle, { outlined: false, colors: heroColors, fallbackShadow: shadows.sm })}
                  >
                    {hero.primaryCtaLabel}
                  </Button>
                ) : null}
                {showHeroSecondaryCta ? (
                  <Button
                    data-landing-field="hero.secondaryCtaLabel"
                    data-landing-edit-label="Editar botón secundario"
                    variant="outlined"
                    startIcon={hero.secondaryCtaShowIcon !== false ? <HeroCanvasCtaIcon name={hero.secondaryCtaIcon} fallback="storefront" /> : undefined}
                    sx={heroCtaCanvasSx(hero.secondaryCtaStyle, { outlined: true, colors: heroColors, fallbackShadow: "none" })}
                  >
                    {hero.secondaryCtaLabel}
                  </Button>
                ) : null}
              </Stack>
            ) : null}
          </Box>
        </SectionFrame>
      ) : null}

      {segmentAllowed("spotlight") && productsEnabled && sections.showProducts !== false && sections.showSpotlight !== false && catalog.spotlight?.enabled && (spotlightHasContent || studioMode) ? (
        <SectionFrame
          sectionId={layoutInstanceId || "spotlight"}
          sectionType="spotlight"
          layoutOrder={orderFor("spotlight")}
          activeSection={activeSection}
          studioMode={studioMode}
          onSelectSection={onSelectSection}
          onSelectElement={onSelectElement}
          selectedElement={selectedElement}
          onOpenSegmentTool={onOpenSegmentTool}
          onToggleSection={onToggleSection} onRemoveSection={onRemoveSection}
          device={previewDevice.id}
          backgroundSx={sectionBackgroundSx("spotlight")}
          sx={{
            ...segmentCssVariables(theme, "spotlight"),
            ...elementColorSxForSection("spotlight"),
            position: "relative",
            overflow: "hidden",
            p: responsive({ xs: 2 * spotlightSpacingScale, md: 4 * spotlightSpacingScale }, 2 * spotlightSpacingScale),
            color: "#fff",
            background: spotlightBackground
              ? `linear-gradient(90deg, ${colorWithAlpha((catalog.spotlight.secondaryColor || spotlightColors.secondary), "F2")}, ${colorWithAlpha((catalog.spotlight.primaryColor || spotlightColors.primary), "D9")}), url(${spotlightBackground}) ${spotlightBackgroundPosition}/${Math.round(spotlightImageScale * 100)}% auto no-repeat`
              : `linear-gradient(100deg, ${catalog.spotlight.secondaryColor || spotlightColors.secondary}, ${catalog.spotlight.primaryColor || spotlightColors.primary})`,
          }}
        >
          {studioMode ? (
            <Stack
              direction="row"
              spacing={0.6}
              useFlexGap
              flexWrap="wrap"
              sx={{ position: "absolute", top: 10, right: 10, zIndex: 8, justifyContent: "flex-end", maxWidth: "76%" }}
            >
              {studioTarget({ fieldPath: "catalog.spotlight.backgroundImageUrl", label: "Fondo", muted: !spotlightBackground })}
              {!spotlightLogo ? studioTarget({ fieldPath: "catalog.spotlight.logoUrl", label: "Logo", muted: true }) : null}
              {!catalog.spotlight.eyebrow ? studioTarget({ fieldPath: "catalog.spotlight.eyebrow", label: "Texto superior", muted: true }) : null}
              {!catalog.spotlight.title ? studioTarget({ fieldPath: "catalog.spotlight.title", label: "Título", muted: true }) : null}
              {!catalog.spotlight.description ? studioTarget({ fieldPath: "catalog.spotlight.description", label: "Descripción", muted: true }) : null}
              {!showSpotlightCta ? studioTarget({ fieldPath: "catalog.spotlight.ctaLabel", label: "Botón", muted: true }) : null}
            </Stack>
          ) : null}
          <Stack direction={responsive({ xs: "column", sm: "row" }, "column")} spacing={2.4} alignItems={responsive({ xs: "stretch", sm: "center" }, "stretch")} sx={{ position: "relative", zIndex: 1 }}>
            {spotlightLogo ? (
              <Box sx={{ width: responsive({ xs: 210 * spotlightImageScale, sm: 250 * spotlightImageScale }, 210 * spotlightImageScale), p: 1.4, bgcolor: "#fff", borderRadius: 2, flexShrink: 0 }}>
                <SafeCanvasImage
                  src={spotlightLogo}
                  alt={catalog.spotlight.logoAlt || ""}
                  sx={{ width: "100%", height: 72 * spotlightImageScale, objectFit: "contain" }}
                  {...editableImageProps("spotlight", "catalog.spotlight.logoUrl", "Editar logo o imagen destacada")}
                />
              </Box>
            ) : null}
            {catalog.spotlight.eyebrow || catalog.spotlight.title || catalog.spotlight.description ? (
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {catalog.spotlight.eyebrow ? <Typography data-landing-field="catalog.spotlight.eyebrow" data-landing-edit-label="Editar texto superior" variant="overline" sx={{ opacity: .75 }}>{catalog.spotlight.eyebrow}</Typography> : null}
                {catalog.spotlight.title ? <Typography data-landing-field="catalog.spotlight.title" data-landing-edit-label="Editar título" sx={{ fontSize: responsive({ xs: 30 * (spotlightTitleScale * titleWordFitScale(catalog.spotlight.title)), md: 34 * (spotlightTitleScale * titleWordFitScale(catalog.spotlight.title)) }, 30 * (spotlightTitleScale * titleWordFitScale(catalog.spotlight.title))), overflowWrap: "normal", wordBreak: "normal", hyphens: "none", fontWeight: "var(--wp-segment-title-weight)", letterSpacing: "var(--wp-segment-letter-spacing)" }}>{capitalizeSectionTitle(catalog.spotlight.title)}</Typography> : null}
                {catalog.spotlight.description ? <Typography data-landing-field="catalog.spotlight.description" data-landing-edit-label="Editar descripción" sx={{ mt: catalog.spotlight.title || catalog.spotlight.eyebrow ? .8 : 0, opacity: .82 }}>{catalog.spotlight.description}</Typography> : null}
              </Box>
            ) : null}
            {showSpotlightCta ? (
              <Button data-landing-field="catalog.spotlight.ctaLabel" data-landing-edit-label="Editar botón destacado" variant="contained" sx={{ bgcolor: "#fff", color: catalog.spotlight.primaryColor || spotlightColors.primary, textTransform: "none", flexShrink: 0 }}>{catalog.spotlight.ctaLabel}</Button>
            ) : null}
          </Stack>
        </SectionFrame>
      ) : null}

      {segmentAllowed("featured") && productsEnabled && sections.showProducts !== false && sections.showFeatured !== false ? (
        <SectionFrame sectionId={layoutInstanceId || "featured"} sectionType="featured" layoutOrder={orderFor("featured")} activeSection={activeSection} selectedElement={selectedElement} studioMode={studioMode} onSelectSection={onSelectSection} onSelectElement={onSelectElement} onOpenSegmentTool={onOpenSegmentTool} onToggleSection={onToggleSection} onRemoveSection={onRemoveSection} device={previewDevice.id} backgroundSx={sectionBackgroundSx("featured")} sx={{ ...segmentCssVariables(theme, "featured"), px: horizontalPadding, py: responsive({ xs: px(finite(spacing.xl, 32) * featuredSpacingScale, 32), md: px(finite(spacing.section, 72) * featuredSpacingScale, 72) }, px(finite(spacing.xl, 32) * featuredSpacingScale, 32)), bgcolor: catalog.featured?.backgroundColor || "#FFFFFF" }}>
          {catalog.featured?.eyebrow ? <Typography data-landing-field="catalog.featured.eyebrow" variant="overline" sx={{ color: catalog.featured?.eyebrowColor || "#F35A3A", fontWeight: 900 }}>{catalog.featured.eyebrow}</Typography> : studioTarget({ fieldPath: "catalog.featured.eyebrow", label: "Texto superior", muted: true })}
          {catalog.featured?.title ? <Typography data-landing-field="catalog.featured.title" sx={{ color: catalog.featured?.titleColor || "#19242F", fontSize: responsive({ xs: 30 * (featuredTitleScale * titleWordFitScale(catalog.featured.title)), md: 34 * (featuredTitleScale * titleWordFitScale(catalog.featured.title)) }, 30 * (featuredTitleScale * titleWordFitScale(catalog.featured.title))), overflowWrap: "normal", wordBreak: "normal", hyphens: "none", fontWeight: "var(--wp-segment-title-weight)", letterSpacing: "var(--wp-segment-letter-spacing)" }}>{capitalizeSectionTitle(catalog.featured.title)}</Typography> : studioTarget({ fieldPath: "catalog.featured.title", label: "Título", muted: true })}
          {catalog.featured?.subtitle ? <Typography data-landing-field="catalog.featured.subtitle" sx={{ mt: catalog.featured?.title || catalog.featured?.eyebrow ? .6 : 0, color: catalog.featured?.subtitleColor || "#5B5D73" }}>{catalog.featured.subtitle}</Typography> : studioTarget({ fieldPath: "catalog.featured.subtitle", label: "Descripción", muted: true })}
          <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mt: 1 }}>
            {catalog.featured?.ctaLabel ? (
              <Button
                size="small"
                variant="outlined"
                data-landing-field="catalog.featured.ctaLabel"
                data-landing-element-type="button"
                data-landing-edit-label="Editar botón de catálogo"
                title="Acción del catálogo"
                sx={{ textTransform: "none", fontWeight: 850, borderRadius: 999 }}
              >
                {catalog.featured.ctaLabel}
              </Button>
            ) : studioTarget({ fieldPath: "catalog.featured.ctaLabel", label: "Botón de catálogo", muted: true })}
            {studioMode ? <Chip size="small" label={gastronomicHome ? "Carta" : "Catálogo"} variant="outlined" /> : null}
          </Stack>
          <PlaceholderCards
            labels={gastronomicHome ? ["Plato 1", "Plato 2", "Plato 3"] : ["Producto o servicio 1", "Producto o servicio 2", "Producto o servicio 3"]}
            primary={primary}
            surface={surface}
            renderDefaults={{ colors, typography, spacing, borders, shadows }}
            device={previewDevice.id}
            cardConfig={catalogCards}
            visualStyle={segmentVisualStyleForSection("featured")}
            sectionBackgroundColor={catalog.featured?.backgroundColor || "#FFFFFF"}
            sectionHasBackgroundImage={Boolean(resolveLandingAssetUrl(segmentVisualStyleForSection("featured")?.backgroundImageUrl))}
            sectionBackgroundComplex={false}
            imageScale={featuredImageScale}
            spacingScale={featuredSpacingScale}
            landingElementId="featured.cards"
          />
        </SectionFrame>
      ) : null}

      {segmentAllowed("brands") && productsEnabled && sections.showProducts !== false && sections.showBrands !== false ? (
        <SectionFrame sectionId={layoutInstanceId || "brands"} sectionType="brands" layoutOrder={orderFor("brands")} activeSection={activeSection} selectedElement={selectedElement} studioMode={studioMode} onSelectSection={onSelectSection} onSelectElement={onSelectElement} onOpenSegmentTool={onOpenSegmentTool} onToggleSection={onToggleSection} onRemoveSection={onRemoveSection} device={previewDevice.id} backgroundSx={sectionBackgroundSx("brands")} sx={{ ...segmentCssVariables(theme, "brands"), ...elementColorSxForSection("brands"), px: horizontalPadding, py: px(finite(spacing.xl, 32) * brandsSpacingScale, 32), bgcolor: brandsColors.surface }}>
          {catalog.brands?.eyebrow ? <Typography data-landing-field="catalog.brands.eyebrow" variant="overline" sx={{ color: brandsColors.primary, fontWeight: 900 }}>{catalog.brands.eyebrow}</Typography> : studioTarget({ fieldPath: "catalog.brands.eyebrow", label: "Texto superior", muted: true })}
          {catalog.brands?.title ? <Typography data-landing-field="catalog.brands.title" sx={{ color: featuredColors.secondary, fontSize: 30 * (brandsTitleScale * titleWordFitScale(catalog.brands.title)), overflowWrap: "normal", wordBreak: "normal", hyphens: "none", fontWeight: "var(--wp-segment-title-weight)", letterSpacing: "var(--wp-segment-letter-spacing)" }}>{capitalizeSectionTitle(catalog.brands.title)}</Typography> : studioTarget({ fieldPath: "catalog.brands.title", label: "Título", muted: true })}
          {catalog.brands?.subtitle ? <Typography data-landing-field="catalog.brands.subtitle" sx={{ mt: catalog.brands?.title || catalog.brands?.eyebrow ? .6 : 0, color: brandsColors.mutedText }}>{catalog.brands.subtitle}</Typography> : studioTarget({ fieldPath: "catalog.brands.subtitle", label: "Descripción", muted: true })}

          {configuredBrands.length ? (
            <LandingPreviewHorizontalTrack
              mt={catalog.brands?.eyebrow || catalog.brands?.title || catalog.brands?.subtitle ? 1.8 : 0}
              gap={1.6}
              ariaLabel="Vista previa horizontal de marcas"
              sx={{
                px: 0.1,
                pb: 0.6,
              }}
            >
              {configuredBrands.map(({ item, sourceIndex }, index) => {
                const label = item?.name ?? item?.label ?? "";
                const brandLogo = resolveLandingAssetUrl(item?.logoUrl);
                const itemPath = `catalog.brands.items.${sourceIndex}`;
                return (
                  <Paper
                    key={`${label}-${sourceIndex >= 0 ? sourceIndex : index}`}
                    component={studioMode ? "button" : "div"}
                    type={studioMode ? "button" : undefined}
                    aria-label={studioMode ? `Editar marca ${label || sourceIndex + 1}` : undefined}
                    data-landing-field={studioMode ? itemPath : undefined}
                    data-landing-element-type={studioMode ? "collection" : undefined}
                    data-landing-edit-label={studioMode ? `Editar marca ${label || sourceIndex + 1}` : undefined}
                    elevation={0}
                    sx={{
                      flex: "0 0 auto",
                      width: responsive({ xs: 170, sm: 210, md: 235 }, 170),
                      height: responsive({ xs: 108 * brandsImageScale, sm: 140 * brandsImageScale, md: 155 * brandsImageScale }, 108 * brandsImageScale),
                      px: 1.5,
                      py: 1.2,
                      display: "grid",
                      placeItems: "center",
                      borderRadius: px(borders.radiusMd, 10),
                      bgcolor: brandsColors.background,
                      border: cardBorder,
                      boxShadow: shadows.sm,
                      font: "inherit",
                      color: "inherit",
                      cursor: studioMode ? "pointer" : "default",
                      appearance: "none",
                      textAlign: "inherit",
                    }}
                  >
                    {brandLogo ? (
                      <SafeCanvasImage
                        src={brandLogo}
                        alt={item?.alt || label}
                        sx={{ width: "100%", height: "100%", maxHeight: "100%", objectFit: "contain" }}
                      />
                    ) : (
                      <Typography variant="body2" sx={{ textAlign: "center", fontWeight: 850, color: brandsColors.secondary }}>{label || `Marca ${sourceIndex + 1}`}</Typography>
                    )}
                  </Paper>
                );
              })}
            </LandingPreviewHorizontalTrack>
          ) : (
            <Stack sx={{ mt: catalog.brands?.eyebrow || catalog.brands?.title || catalog.brands?.subtitle ? 1.8 : 0 }}>
              {studioTarget({ elementId: "brands.items", label: "Agregar marcas", count: 0 })}
            </Stack>
          )}

          {studioMode ? (
            <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mt: 1.4 }}>
              {configuredBrands.length ? studioTarget({ elementId: "brands.items", label: "Administrar marcas", count: configuredBrands.length }) : null}
              <Button
                size="small"
                variant="outlined"
                data-landing-field="catalog.brands.ctaLabel"
                data-landing-element-type="button"
                data-landing-edit-label="Editar acción de marca"
                sx={{ textTransform: "none", fontWeight: 850, borderRadius: 999 }}
              >
                Acción: {catalog.brands?.ctaLabel || "Abrir marca"}
              </Button>
              <Chip size="small" label="Abre catálogo" variant="outlined" />
            </Stack>
          ) : null}
        </SectionFrame>
      ) : null}

      {segmentAllowed("categories") && productsEnabled && sections.showProducts !== false && sections.showCategories !== false ? (
        <SectionFrame
          sectionId={layoutInstanceId || "categories"}
          sectionType="categories"
          layoutOrder={orderFor("categories")}
          activeSection={activeSection}
          selectedElement={selectedElement}
          studioMode={studioMode}
          onSelectSection={onSelectSection}
          onSelectElement={onSelectElement}
          onOpenSegmentTool={onOpenSegmentTool}
          onToggleSection={onToggleSection} onRemoveSection={onRemoveSection}
          device={previewDevice.id}
          sx={{
            ...segmentCssVariables(theme, "categories"),
            px: horizontalPadding,
            py: px(finite(spacing.xl, 32) * categoriesSpacingScale, 32),
            bgcolor: categoriesVisual.backgroundColor,
            ...(categoriesBackground ? {
              backgroundImage: `url("${categoriesBackground.replace(/"/g, '\\"')}")`,
              backgroundSize: "cover",
              backgroundPosition: categoriesBackgroundPosition,
              backgroundRepeat: "no-repeat",
            } : {}),
          }}
        >
          {catalog.categories?.eyebrow ? <Typography data-landing-field="catalog.categories.eyebrow" variant="overline" sx={{ color: categoriesVisual.eyebrowColor, fontWeight: 900 }}>{catalog.categories.eyebrow}</Typography> : null}
          {catalog.categories?.title ? <Typography data-landing-field="catalog.categories.title" sx={{ color: categoriesVisual.titleColor, fontSize: 30 * (categoriesTitleScale * titleWordFitScale(catalog.categories.title)), overflowWrap: "normal", wordBreak: "normal", hyphens: "none", fontWeight: "var(--wp-segment-title-weight)", letterSpacing: "var(--wp-segment-letter-spacing)" }}>{capitalizeSectionTitle(catalog.categories.title)}</Typography> : null}
          {catalog.categories?.subtitle ? <Typography data-landing-field="catalog.categories.subtitle" sx={{ mt: catalog.categories?.title || catalog.categories?.eyebrow ? .6 : 0, color: categoriesVisual.subtitleColor }}>{catalog.categories.subtitle}</Typography> : null}

          {studioMode && (!catalog.categories?.eyebrow || !catalog.categories?.title || !catalog.categories?.subtitle) ? (
            <Stack direction="row" spacing={0.7} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
              {!catalog.categories?.eyebrow ? studioTarget({ fieldPath: "catalog.categories.eyebrow", label: "Texto superior", muted: true }) : null}
              {!catalog.categories?.title ? studioTarget({ fieldPath: "catalog.categories.title", label: "Título", muted: true }) : null}
              {!catalog.categories?.subtitle ? studioTarget({ fieldPath: "catalog.categories.subtitle", label: "Descripción", muted: true }) : null}
            </Stack>
          ) : null}

          <Stack direction="row" alignItems="center" spacing={0.8} useFlexGap flexWrap="wrap" sx={{ mt: catalog.categories?.title || catalog.categories?.eyebrow || catalog.categories?.subtitle ? 1.5 : 0 }}>
            {studioMode ? <Chip size="small" label={gastronomicHome ? "Se genera desde tu carta" : "Se genera desde tu catálogo"} variant="outlined" /> : null}
            {studioMode ? studioTarget({ elementId: "categories.item-naming", label: "Cómo llamamos a tus ítems" }) : null}
          </Stack>

          <LandingPreviewHorizontalTrack mt={1.2} gap={2} ariaLabel="Vista previa horizontal de categorías" sx={{ pb: 0.6 }}>
            {[0, 1, 2].map((item) => {
              const singular = String(catalog.categories?.productSingular || "producto").trim() || "producto";
              const plural = String(catalog.categories?.productPlural || "productos").trim() || "productos";
              const count = item === 0 ? 1 : (item + 1) * 6;
              const fallback = `linear-gradient(${categoriesVisual.fallbackAngle}deg, ${categoriesVisual.fallbackStartColor}, ${categoriesVisual.fallbackEndColor})`;
              const overlay = `linear-gradient(180deg, ${categoriesVisual.overlayTopColor} 0%, ${categoriesVisual.overlayMiddleColor} 38%, ${categoriesVisual.overlayBottomColor} 100%)`;
              const shadow = `${categoriesVisual.cardShadowX}px ${categoriesVisual.cardShadowY}px ${categoriesVisual.cardShadowBlur}px ${categoriesVisual.cardShadowSpread}px ${cssColorWithOpacity(categoriesVisual.cardShadowColor, categoriesVisual.cardShadowOpacity)}`;
              const titleShadow = `${categoriesVisual.cardTitleShadowX}px ${categoriesVisual.cardTitleShadowY}px ${categoriesVisual.cardTitleShadowBlur}px ${cssColorWithOpacity(categoriesVisual.cardTitleShadowColor, categoriesVisual.cardTitleShadowOpacity)}`;
              return (
                <Paper
                  key={item}
                  component={studioMode ? "button" : "div"}
                  type={studioMode ? "button" : undefined}
                  data-landing-element-id={studioMode ? "categories.generated" : undefined}
                  data-landing-element-type={studioMode ? "complex" : undefined}
                  data-landing-edit-label={studioMode ? "Configurar presentación de categorías" : undefined}
                  aria-label={studioMode ? "Categoría generada desde tu catálogo. Configurar presentación" : undefined}
                  elevation={0}
                  sx={{
                    position: "relative",
                    flex: "0 0 auto",
                    width: responsive({ xs: 270, sm: 330, md: 390 }, 270),
                    height: responsive({ xs: 230 * categoriesImageScale, sm: 300 * categoriesImageScale, md: 325 * categoriesImageScale }, 230 * categoriesImageScale),
                    minWidth: 0,
                    overflow: "hidden",
                    textAlign: "left",
                    bgcolor: categoriesVisual.cardBackgroundColor,
                    border: `1px solid ${categoriesVisual.cardBorderColor}`,
                    borderRadius: px(categoriesVisual.cardBorderRadius, 4),
                    boxShadow: shadow,
                    cursor: studioMode ? "pointer" : "default",
                    font: "inherit",
                    appearance: "none",
                  }}
                >
                  <Box sx={{ position: "absolute", inset: 0, background: fallback, backgroundSize: categoriesVisual.imageFit, backgroundPosition: categoriesVisual.imagePosition }} />
                  <Box sx={{ position: "absolute", inset: 0, background: overlay }} />
                  <Box sx={{ position: "absolute", inset: 0, p: responsive({ xs: 2, sm: 2.5 }, 2), display: "flex", flexDirection: "column", justifyContent: "flex-end", minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="flex-end" flexWrap="wrap" useFlexGap sx={{ minWidth: 0 }}>
                      <Typography sx={{ flex: "1 1 160px", color: categoriesVisual.cardTitleColor, textShadow: titleShadow, fontWeight: 900, fontSize: responsive({ xs: "1.35rem", sm: "1.55rem", md: "1.75rem" }, "1.35rem"), overflowWrap: "normal", wordBreak: "normal" }}>
                        {`Ejemplo ${item + 1}`}
                      </Typography>
                      <Chip size="small" label={`${count} ${count === 1 ? singular : plural}`} sx={{ bgcolor: categoriesVisual.countBackgroundColor, color: categoriesVisual.countTextColor, border: `1px solid ${categoriesVisual.countBorderColor}` }} />
                    </Stack>
                    {catalog.categories?.ctaLabel ? (
                      <Button
                        size="small"
                        variant="contained"
                        tabIndex={studioMode ? -1 : undefined}
                        sx={{ mt: 1.5, alignSelf: "flex-start", textTransform: "none", fontWeight: 850, borderRadius: px(categoriesVisual.ctaBorderRadius, 999), bgcolor: categoriesVisual.ctaBackgroundColor, color: categoriesVisual.ctaTextColor, border: `1px solid ${categoriesVisual.ctaBorderColor}`, "&:hover": { bgcolor: categoriesVisual.ctaHoverBackgroundColor, color: categoriesVisual.ctaHoverTextColor } }}
                      >
                        {catalog.categories.ctaLabel}
                      </Button>
                    ) : null}
                  </Box>
                </Paper>
              );
            })}
          </LandingPreviewHorizontalTrack>

          {studioMode ? (
            <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mt: 1.2 }}>
              {studioTarget({ elementId: "categories.generated", label: gastronomicHome ? "Secciones de la carta" : "Categorías automáticas" })}
              <Chip size="small" label={gastronomicHome ? "Abre Carta" : "Abre categoría"} variant="outlined" />
            </Stack>
          ) : null}
        </SectionFrame>
      ) : null}

      {segmentAllowed("about") && sections.showAbout !== false && (aboutHasContent || studioMode) ? (
        <SectionFrame
          sectionId={layoutInstanceId || "about"}
          sectionType="about"
          layoutOrder={orderFor("about")}
          activeSection={activeSection}
          studioMode={studioMode}
          onSelectSection={onSelectSection}
          onSelectElement={onSelectElement}
          selectedElement={selectedElement}
          onOpenSegmentTool={onOpenSegmentTool}
          onToggleSection={onToggleSection} onRemoveSection={onRemoveSection}
          device={previewDevice.id}
          sx={{
            ...segmentCssVariables(theme, "about"),
            display: "grid",
            gridTemplateColumns: responsive({
              xs: "minmax(0, 1fr)",
              sm: aboutImage && aboutHasCopy ? "repeat(2, minmax(0, 1fr))" : "minmax(0, 1fr)",
            }, "minmax(0, 1fr)"),
            gap: responsive({ xs: 0, sm: px(finite(spacing.xl, 32) * aboutSpacingScale, 32), md: px(finite(spacing.xxl, 48) * aboutSpacingScale, 48) }, 0),
            alignItems: "center",
            bgcolor: aboutVisual.backgroundColor,
          }}
        >
          {studioMode ? (
            <Stack
              direction="row"
              spacing={0.6}
              useFlexGap
              flexWrap="wrap"
              sx={{ position: "absolute", top: 10, right: 10, zIndex: 8, justifyContent: "flex-end", maxWidth: "78%" }}
            >
              {!about.eyebrow ? studioTarget({ fieldPath: "about.eyebrow", label: "Texto superior", muted: true }) : null}
              {!about.title ? studioTarget({ fieldPath: "about.title", label: "Título", muted: true }) : null}
              {!about.description ? studioTarget({ fieldPath: "about.description", label: "Descripción", muted: true }) : null}
              {!aboutImage ? studioTarget({ fieldPath: "about.imageUrl", label: "Imagen", muted: true }) : null}
              {!showAboutCta ? studioTarget({ fieldPath: "about.ctaLabel", label: "Botón", muted: true }) : null}
            </Stack>
          ) : null}
          {aboutImage ? (
          <Box
            data-landing-field="about.imageUrl"
            data-landing-element-type="image"
            data-landing-edit-label="Editar imagen"
            sx={{
              width: "100%",
              aspectRatio: responsive({ xs: "4 / 3", md: "6 / 5" }, "4 / 3"),
              minHeight: responsive({ xs: 300 * aboutImageScale, md: 480 * aboutImageScale }, 300 * aboutImageScale),
              maxHeight: 560 * aboutImageScale,
              order: about.layout === "image-right" && aboutImage && aboutHasCopy && !mobileLayout ? 2 : 1,
              bgcolor: aboutVisual.imageBackgroundColor,
              backgroundImage: aboutImage ? `url(${aboutImage})` : "none",
              backgroundSize: "cover",
              backgroundPosition: normalizeImagePosition(about.imagePosition),
              borderRadius: `${aboutVisual.imageBorderRadius}px`,
              boxShadow: aboutVisual.imageShadow,
              ...(studioMode && aboutImage ? {
                cursor: "pointer",
                outline: "2px solid transparent",
                outlineOffset: -2,
                transition: "outline-color .15s ease, filter .15s ease",
                "&:hover": { outlineColor: "rgba(243,90,58,.92)", filter: "brightness(.96)" },
                "&:focus-visible": { outlineColor: "rgba(243,90,58,1)" },
              } : {}),
            }}
          />
          ) : null}
          {(aboutHasCopy || studioMode) ? (
            <Box
              sx={{
                p: responsive({ xs: px(finite(spacing.xl, 32) * aboutSpacingScale, 32), md: px(finite(spacing.section, 72) * aboutSpacingScale, 72) }, px(finite(spacing.xl, 32) * aboutSpacingScale, 32)),
                alignSelf: "center",
                order: about.layout === "image-right" && aboutImage && aboutHasCopy && !mobileLayout ? 1 : 2,
                bgcolor: aboutVisual.contentBackgroundColor,
                borderRadius: aboutVisual.contentBackgroundColor !== "transparent" ? `${aboutVisual.contentBorderRadius}px` : 0,
              }}
            >
              {about.eyebrow ? <Typography data-landing-field="about.eyebrow" data-landing-edit-label="Editar texto superior" variant="overline" sx={{ color: aboutVisual.eyebrowColor, fontWeight: 900 }}>{about.eyebrow}</Typography> : null}
              {about.title ? <Typography data-landing-field="about.title" data-landing-edit-label="Editar título" sx={{ color: aboutVisual.titleColor, fontSize: responsive({ xs: 30 * (aboutTitleScale * titleWordFitScale(about.title)), md: 34 * (aboutTitleScale * titleWordFitScale(about.title)) }, 30 * (aboutTitleScale * titleWordFitScale(about.title))), overflowWrap: "normal", wordBreak: "normal", hyphens: "none", fontWeight: "var(--wp-segment-title-weight)", letterSpacing: "var(--wp-segment-letter-spacing)" }}>{capitalizeSectionTitle(about.title)}</Typography> : null}
              {about.description ? <Typography data-landing-field="about.description" data-landing-edit-label="Editar descripción" sx={{ mt: about.title || about.eyebrow ? 1.2 : 0, color: aboutVisual.descriptionColor, lineHeight: 1.7, whiteSpace: "pre-line" }}>{about.description}</Typography> : null}
              {showAboutCta ? <Button data-landing-field="about.ctaLabel" data-landing-element-type="button" data-landing-edit-label="Editar botón" variant="contained" sx={{ mt: about.title || about.eyebrow || about.description ? 2.2 : 0, bgcolor: aboutVisual.ctaBackgroundColor, color: aboutVisual.ctaTextColor, border: `${aboutVisual.ctaBorderWidth}px solid ${aboutVisual.ctaBorderColor}`, borderRadius: `${aboutVisual.ctaBorderRadius}px`, textTransform: "none", boxShadow: aboutVisual.ctaShadow, filter: "none", "&:hover": { bgcolor: aboutVisual.ctaHoverBackgroundColor, color: aboutVisual.ctaHoverTextColor, boxShadow: aboutVisual.ctaHoverShadow, filter: "none", transform: "translateY(-1px)" }, "&:focus-visible": { boxShadow: aboutVisual.ctaFocusShadow }, "&:active": { transform: "scale(.98)" } }}>{about.ctaLabel}</Button> : null}
            </Box>
          ) : null}
        </SectionFrame>
      ) : null}

      {segmentAllowed("highlights") && sections.showHighlights !== false && (highlightsHasContent || studioMode) ? (
        <SectionFrame
          sectionId={layoutInstanceId || "highlights"}
          sectionType="highlights"
          layoutOrder={orderFor("highlights")}
          activeSection={activeSection}
          studioMode={studioMode}
          onSelectSection={onSelectSection}
          onSelectElement={onSelectElement}
          selectedElement={selectedElement}
          onOpenSegmentTool={onOpenSegmentTool}
          onToggleSection={onToggleSection} onRemoveSection={onRemoveSection}
          device={previewDevice.id}
          sx={{
            ...segmentCssVariables(theme, "highlights"),
            position: "relative",
            overflow: "hidden",
            px: horizontalPadding,
            py: responsive(
              { xs: px(finite(spacing.xl, 32) * highlightsSpacingScale, 32), md: px(finite(spacing.section, 72) * highlightsSpacingScale, 72) },
              px(finite(spacing.xl, 32) * highlightsSpacingScale, 32)
            ),
            bgcolor: highlightsVisual.backgroundColor,
          }}
        >
          {highlightsBackground ? (
            <Box
              aria-hidden
              sx={{
                position: "absolute",
                inset: 0,
                backgroundImage: `url(${highlightsBackground})`,
                backgroundSize: "cover",
                backgroundPosition: highlightsBackgroundPosition,
                backgroundRepeat: "no-repeat",
                opacity: highlightsBackgroundOpacity,
                pointerEvents: "none",
              }}
            />
          ) : null}
          <Box sx={{ position: "relative", zIndex: 1 }}>
            {highlightsIntro.eyebrow ? <Typography data-landing-field="highlightsIntro.eyebrow" data-landing-edit-label="Editar texto superior" variant="overline" sx={{ color: highlightsVisual.eyebrowColor, fontWeight: 900 }}>{highlightsIntro.eyebrow}</Typography> : null}
            {highlightsIntro.title ? <Typography data-landing-field="highlightsIntro.title" data-landing-edit-label="Editar título" sx={{ color: highlightsVisual.titleColor, fontSize: responsive({ xs: 30 * (highlightsTitleScale * titleWordFitScale(highlightsIntro.title)), md: 34 * (highlightsTitleScale * titleWordFitScale(highlightsIntro.title)) }, 30 * (highlightsTitleScale * titleWordFitScale(highlightsIntro.title))), overflowWrap: "normal", wordBreak: "normal", hyphens: "none", fontWeight: "var(--wp-segment-title-weight)", letterSpacing: "var(--wp-segment-letter-spacing)" }}>{capitalizeSectionTitle(highlightsIntro.title)}</Typography> : null}
            {highlightsIntro.subtitle ? <Typography data-landing-field="highlightsIntro.subtitle" data-landing-edit-label="Editar descripción" sx={{ mt: highlightsIntro.title || highlightsIntro.eyebrow ? .7 : 0, color: highlightsVisual.subtitleColor }}>{highlightsIntro.subtitle}</Typography> : null}
            {studioMode && (!highlightsIntro.eyebrow || !highlightsIntro.title || !highlightsIntro.subtitle) ? (
              <Stack direction="row" spacing={0.7} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                {!highlightsIntro.eyebrow ? studioTarget({ fieldPath: "highlightsIntro.eyebrow", label: "Texto superior", muted: true }) : null}
                {!highlightsIntro.title ? studioTarget({ fieldPath: "highlightsIntro.title", label: "Título", muted: true }) : null}
                {!highlightsIntro.subtitle ? studioTarget({ fieldPath: "highlightsIntro.subtitle", label: "Descripción", muted: true }) : null}
              </Stack>
            ) : null}
            {studioMode ? (
              <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" sx={{ mt: highlightsIntro.eyebrow || highlightsIntro.title || highlightsIntro.subtitle ? 1.2 : 0 }}>
                {studioTarget({ elementId: "highlights.collection", label: "Diferenciales", count: highlights.length })}
                <Chip size="small" label="Editar tarjetas" variant="outlined" />
              </Stack>
            ) : null}
            {visibleHighlights.length ? (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: responsive(
                    {
                      xs: "minmax(0, 1fr)",
                      sm: visibleHighlights.length === 1 ? "minmax(0, 1fr)" : "repeat(2, minmax(0, 1fr))",
                      md: `repeat(${Math.max(1, Math.min(visibleHighlights.length, 4))}, minmax(0, 1fr))`,
                    },
                    "minmax(0, 1fr)"
                  ),
                  gap: responsive({ xs: 1.4, sm: 1.6, md: 2 }, 1.4),
                  mt: highlightsIntro.eyebrow || highlightsIntro.title || highlightsIntro.subtitle ? 2 : 0,
                  alignItems: "stretch",
                }}
              >
                {visibleHighlights.map(({ item, index }) => {
                  const highlightImage = resolveLandingAssetUrl(item.imageUrl);
                  const highlightVideo = youtubeEmbedUrl(item.youtubeUrl);
                  const cardStyle = item.cardStyle || HIGHLIGHT_VISUAL_DEFAULTS.cardStyle;
                  const iconStyle = item.iconStyle || HIGHLIGHT_VISUAL_DEFAULTS.iconStyle;
                  const iconSurfaceStyle = item.iconSurfaceStyle || HIGHLIGHT_VISUAL_DEFAULTS.iconSurfaceStyle;
                  const titleStyle = item.titleStyle || HIGHLIGHT_VISUAL_DEFAULTS.titleStyle;
                  const descriptionStyle = item.descriptionStyle || HIGHLIGHT_VISUAL_DEFAULTS.descriptionStyle;
                  const imageStyle = item.imageStyle || HIGHLIGHT_VISUAL_DEFAULTS.imageStyle;
                  const videoStyle = item.videoStyle || HIGHLIGHT_VISUAL_DEFAULTS.videoStyle;
                  const contentPadding = finite(cardStyle.contentPadding, HIGHLIGHT_VISUAL_DEFAULTS.cardStyle.contentPadding);
                  const iconSurfaceBorder = `${finite(iconSurfaceStyle.borderWidth, 1)}px solid ${iconSurfaceStyle.borderColor}`;
                  const mediaIconWidth = finite(iconSurfaceStyle.mediaWidth, HIGHLIGHT_VISUAL_DEFAULTS.iconSurfaceStyle.mediaWidth);
                  const mediaIconHeight = finite(iconSurfaceStyle.mediaHeight, HIGHLIGHT_VISUAL_DEFAULTS.iconSurfaceStyle.mediaHeight);
                  return (
                    <Paper
                      key={item.id || `diferencial-${index + 1}`}
                      elevation={0}
                      data-highlight-id={item.id || undefined}
                      data-landing-field={studioMode ? `highlights.${index}` : undefined}
                      data-landing-element-type={studioMode ? "collection-item" : undefined}
                      data-landing-edit-label={studioMode ? `Editar ${item.title || `diferencial ${index + 1}`}` : undefined}
                      sx={{
                        minWidth: 0,
                        height: "100%",
                        overflow: "hidden",
                        borderRadius: `${finite(cardStyle.borderRadius, HIGHLIGHT_VISUAL_DEFAULTS.cardStyle.borderRadius)}px`,
                        bgcolor: cardStyle.backgroundColor,
                        border: `${finite(cardStyle.borderWidth, HIGHLIGHT_VISUAL_DEFAULTS.cardStyle.borderWidth)}px solid ${cardStyle.borderColor}`,
                        boxShadow: cardStyle.boxShadow,
                        transition: "transform .24s ease, box-shadow .24s ease",
                        cursor: studioMode ? "pointer" : "default",
                        "&:hover": {
                          transform: `translateY(${finite(cardStyle.hoverTranslateY, HIGHLIGHT_VISUAL_DEFAULTS.cardStyle.hoverTranslateY)}px)`,
                          boxShadow: cardStyle.hoverBoxShadow,
                        },
                      }}
                    >
                      {highlightVideo ? (
                        <Box
                          component="iframe"
                          src={highlightVideo}
                          title={item.title || `Video del diferencial ${index + 1}`}
                          sx={{ width: "100%", aspectRatio: videoStyle.aspectRatio, display: "block", border: 0, bgcolor: videoStyle.backgroundColor, pointerEvents: studioMode ? "none" : "auto" }}
                        />
                      ) : highlightImage ? (
                        <Box sx={{ position: "relative", bgcolor: imageStyle.backgroundColor }}>
                          <SafeCanvasImage
                            src={highlightImage}
                            alt={item.imageAlt || item.title || ""}
                            sx={{ width: "100%", height: finite(imageStyle.height, HIGHLIGHT_VISUAL_DEFAULTS.imageStyle.height) * highlightsImageScale, objectFit: imageStyle.objectFit, objectPosition: normalizeImagePosition(item.imagePosition), bgcolor: imageStyle.backgroundColor }}
                          />
                          {item.icon ? (
                            <Box
                              sx={{
                                position: "absolute",
                                left: px(finite(spacing.md, 16), 16),
                                bottom: -(mediaIconHeight / 2),
                                width: mediaIconWidth,
                                height: mediaIconHeight,
                                borderRadius: `${finite(iconSurfaceStyle.mediaBorderRadius, HIGHLIGHT_VISUAL_DEFAULTS.iconSurfaceStyle.mediaBorderRadius)}px`,
                                display: "grid",
                                placeItems: "center",
                                color: iconStyle.color,
                                bgcolor: iconSurfaceStyle.floatingBackgroundColor,
                                border: iconSurfaceBorder,
                                boxShadow: iconSurfaceStyle.floatingBoxShadow,
                              }}
                            >
                              <HighlightCanvasIcon name={item.icon} sx={{ fontSize: finite(iconStyle.size, HIGHLIGHT_VISUAL_DEFAULTS.iconStyle.size) }} />
                            </Box>
                          ) : null}
                        </Box>
                      ) : item.icon ? (
                        <Box sx={{ px: `${contentPadding}px`, pt: `${contentPadding}px` }}>
                          <Box
                            sx={{
                              width: finite(iconSurfaceStyle.width, HIGHLIGHT_VISUAL_DEFAULTS.iconSurfaceStyle.width),
                              height: finite(iconSurfaceStyle.height, HIGHLIGHT_VISUAL_DEFAULTS.iconSurfaceStyle.height),
                              borderRadius: `${finite(iconSurfaceStyle.borderRadius, HIGHLIGHT_VISUAL_DEFAULTS.iconSurfaceStyle.borderRadius)}px`,
                              display: "grid",
                              placeItems: "center",
                              color: iconStyle.color,
                              bgcolor: iconSurfaceStyle.backgroundColor,
                              border: iconSurfaceBorder,
                              boxShadow: iconSurfaceStyle.boxShadow,
                            }}
                          >
                            <HighlightCanvasIcon name={item.icon} sx={{ fontSize: finite(iconStyle.size, HIGHLIGHT_VISUAL_DEFAULTS.iconStyle.size) }} />
                          </Box>
                        </Box>
                      ) : null}
                      {highlightVideo && item.icon ? (
                        <Box sx={{ px: `${contentPadding}px`, pt: `${Math.max(12, contentPadding * .72)}px` }}>
                          <Box sx={{ width: mediaIconWidth, height: mediaIconHeight, borderRadius: `${finite(iconSurfaceStyle.mediaBorderRadius, HIGHLIGHT_VISUAL_DEFAULTS.iconSurfaceStyle.mediaBorderRadius)}px`, display: "grid", placeItems: "center", color: iconStyle.color, bgcolor: iconSurfaceStyle.backgroundColor, border: iconSurfaceBorder, boxShadow: iconSurfaceStyle.boxShadow }}>
                            <HighlightCanvasIcon name={item.icon} sx={{ fontSize: finite(iconStyle.size, HIGHLIGHT_VISUAL_DEFAULTS.iconStyle.size) }} />
                          </Box>
                        </Box>
                      ) : null}
                      <Box sx={{
                        p: `${contentPadding}px`,
                        pt: highlightImage && item.icon
                          ? `${contentPadding + Math.round(mediaIconHeight / 3)}px`
                          : highlightVideo && item.icon
                            ? `${Math.max(12, contentPadding * .7)}px`
                            : (highlightImage || highlightVideo)
                              ? `${contentPadding}px`
                              : item.icon
                                ? `${Math.max(12, contentPadding * .72)}px`
                                : `${contentPadding}px`,
                      }}>
                        {item.title ? <Typography data-landing-color-field={`highlights.${index}.title`} sx={{ color: titleStyle.color, fontSize: finite(titleStyle.fontSize, HIGHLIGHT_VISUAL_DEFAULTS.titleStyle.fontSize) * titleWordFitScale(item.title), fontWeight: finite(titleStyle.fontWeight, HIGHLIGHT_VISUAL_DEFAULTS.titleStyle.fontWeight), lineHeight: finite(titleStyle.lineHeight, HIGHLIGHT_VISUAL_DEFAULTS.titleStyle.lineHeight), overflowWrap: "normal", wordBreak: "normal", hyphens: "none" }}>{item.title}</Typography> : null}
                        {item.description ? <Typography data-landing-color-field={`highlights.${index}.description`} sx={{ mt: item.title ? .6 : 0, color: descriptionStyle.color, fontSize: finite(descriptionStyle.fontSize, HIGHLIGHT_VISUAL_DEFAULTS.descriptionStyle.fontSize), fontWeight: finite(descriptionStyle.fontWeight, HIGHLIGHT_VISUAL_DEFAULTS.descriptionStyle.fontWeight), lineHeight: finite(descriptionStyle.lineHeight, HIGHLIGHT_VISUAL_DEFAULTS.descriptionStyle.lineHeight), whiteSpace: "pre-line" }}>{item.description}</Typography> : null}
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            ) : null}
          </Box>
        </SectionFrame>
      ) : null}

      {segmentAllowed("custom") && (studioMode || custom.eyebrow || custom.title || custom.description || custom.imageUrl || custom.youtubeUrl || custom.calendlyUrl || custom.ctaLabel || custom.ctaUrl) ? (
        <SectionFrame
          sectionId={layoutInstanceId || "custom"}
          sectionType="custom"
          sectionLabel={custom.title || "Sección libre"}
          layoutOrder={orderFor("custom")}
          activeSection={activeSection}
          studioMode={studioMode}
          onSelectSection={onSelectSection}
          onSelectElement={onSelectElement}
          selectedElement={selectedElement}
          onOpenSegmentTool={onOpenSegmentTool}
          onToggleSection={onToggleSection} onRemoveSection={onRemoveSection}
          device={previewDevice.id}
          backgroundSx={{}}
          sx={{
            px: horizontalPadding,
            py: responsive({ xs: `${6 * customSegmentStyle.custom.layout.spacingScale}rem`, md: `${9 * customSegmentStyle.custom.layout.spacingScale}rem` }, `${6 * customSegmentStyle.custom.layout.spacingScale}rem`),
            bgcolor: customSegmentStyle.background.color,
            ...(customSegmentStyle.background.imageUrl ? {
              backgroundImage: `url(${resolveLandingAssetUrl(customSegmentStyle.background.imageUrl)})`,
              backgroundPosition: customSegmentStyle.background.imagePosition,
              backgroundSize: customSegmentStyle.background.imageSize,
              backgroundRepeat: "no-repeat",
            } : {}),
          }}
        >
          {(() => {
            const customImage = resolveLandingAssetUrl(custom.imageUrl);
            const customVideo = youtubeEmbedUrl(custom.youtubeUrl);
            const customCalendly = normalizeCalendlyUrl(custom.calendlyUrl);
            const customVisual = customSegmentStyle.custom;
            const customHasMedia = Boolean(customVideo || customCalendly || customImage);
            const customMediaVisible = customHasMedia || studioMode;
            const customCentered = custom.layout === "centered";
            const customContentLeft = custom.layout === "image-right";
            const customContentRight = !customCentered && !customContentLeft;
            const media = customMediaVisible ? (
              <Stack spacing={1.2} sx={{ width: "100%" }}>
                {customCalendly ? (
                  <Box
                    data-landing-element-id={studioMode ? "custom.reservation" : undefined}
                    data-landing-element-type={studioMode ? "complex" : undefined}
                    data-landing-edit-label={studioMode ? "Editar reserva" : undefined}
                    sx={{ borderRadius: `${customVisual.calendly.borderRadius}px`, overflow: "hidden", border: `${customVisual.calendly.borderWidth}px solid ${customVisual.calendly.borderColor}`, boxShadow: customShadow(customVisual.calendly), bgcolor: customVisual.calendly.backgroundColor }}
                  >
                    <Box
                      component="iframe"
                      src={customCalendly}
                      tabIndex={studioMode ? -1 : undefined}
                      title={custom.title ? `Calendly - ${custom.title}` : "Calendly"}
                      sx={{ width: "100%", height: responsive({ xs: customVisual.calendly.heightMobile, md: customVisual.calendly.heightDesktop }, customVisual.calendly.heightMobile), border: 0, bgcolor: customVisual.calendly.backgroundColor, pointerEvents: "none", display: "block" }}
                    />
                  </Box>
                ) : studioTarget({ elementId: "custom.reservation", label: "Reserva" })}
                {customVideo ? (
                  <Box
                    data-landing-element-id={studioMode ? "custom.video" : undefined}
                    data-landing-element-type={studioMode ? "complex" : undefined}
                    data-landing-edit-label={studioMode ? "Editar video" : undefined}
                    sx={{ borderRadius: `${customVisual.youtube.borderRadius}px`, overflow: "hidden", border: `${customVisual.youtube.borderWidth}px solid ${customVisual.youtube.borderColor}`, boxShadow: customShadow(customVisual.youtube), bgcolor: customVisual.youtube.backgroundColor }}
                  >
                    <Box component="iframe" src={customVideo} tabIndex={studioMode ? -1 : undefined} title={custom.title || "Video"} sx={{ width: "100%", aspectRatio: "16 / 9", border: 0, bgcolor: customVisual.youtube.backgroundColor, pointerEvents: "none", display: "block" }} />
                  </Box>
                ) : studioTarget({ elementId: "custom.video", label: "Video" })}
                {customImage ? (
                  <Box
                    data-landing-element-id={studioMode ? "custom.image" : undefined}
                    data-landing-element-type={studioMode ? "image" : undefined}
                    data-landing-edit-label={studioMode ? "Editar imagen" : undefined}
                    sx={{ borderRadius: `${customVisual.image.borderRadius}px`, overflow: "hidden", border: `${customVisual.image.borderWidth}px solid ${customVisual.image.borderColor}`, boxShadow: customShadow(customVisual.image), bgcolor: customVisual.image.backgroundColor }}
                  >
                    <SafeCanvasImage
                      src={customImage}
                      alt={custom.imageAlt || custom.title || ""}
                      sx={{ width: "100%", height: responsive({ xs: customVisual.image.heightMobile, md: customVisual.image.heightDesktop }, customVisual.image.heightMobile), objectFit: customVisual.image.objectFit, objectPosition: normalizeImagePosition(custom.imagePosition), display: "block" }}
                    />
                  </Box>
                ) : studioTarget({ elementId: "custom.image", label: "Imagen" })}
              </Stack>
            ) : null;
            return (
              <Box sx={{ display: "grid", gridTemplateColumns: customCentered ? "minmax(0,1fr)" : responsive({ xs: "minmax(0,1fr)", md: "minmax(0,1fr) minmax(0,1fr)" }, "minmax(0,1fr)"), gap: responsive({ xs: 2, md: 4 }, 2), alignItems: "center" }}>
                {customMediaVisible && !customCentered && customContentRight ? media : null}
                <Stack
                  spacing={1.1}
                  sx={{
                    gridColumn: customCentered ? "1" : responsive({ xs: "1", md: customContentRight ? "2" : "1" }, "1"),
                    maxWidth: customCentered ? 760 : 620,
                    width: "100%",
                    mx: customCentered ? "auto" : 0,
                    justifySelf: customCentered ? "center" : customContentRight ? "end" : "start",
                    textAlign: customCentered ? "center" : customContentRight ? "right" : "left",
                  }}
                >
                  {custom.eyebrow ? (
                    <Typography data-landing-field="custom.eyebrow" data-landing-element-type="text" data-landing-edit-label="Editar texto superior" variant="overline" sx={{ color: customVisual.eyebrow.color, fontWeight: customVisual.eyebrow.fontWeight, letterSpacing: `${customVisual.eyebrow.letterSpacing}em` }}>{custom.eyebrow}</Typography>
                  ) : studioTarget({ fieldPath: "custom.eyebrow", label: "Texto superior", muted: true })}
                  {custom.title ? (
                    <Typography data-landing-field="custom.title" data-landing-element-type="text" data-landing-edit-label="Editar título" sx={{ color: customVisual.title.color, fontSize: responsive({ xs: 30 * customVisual.title.fontScale * titleWordFitScale(custom.title), md: 38 * customVisual.title.fontScale * titleWordFitScale(custom.title) }, 30 * customVisual.title.fontScale * titleWordFitScale(custom.title)), overflowWrap: "normal", wordBreak: "normal", hyphens: "none", fontWeight: customVisual.title.fontWeight }}>{capitalizeSectionTitle(custom.title)}</Typography>
                  ) : studioTarget({ fieldPath: "custom.title", label: "Título" })}
                  {custom.description ? (
                    <Typography data-landing-field="custom.description" data-landing-element-type="text" data-landing-edit-label="Editar descripción" sx={{ color: customVisual.description.color, whiteSpace: "pre-line", lineHeight: customVisual.description.lineHeight }}>{custom.description}</Typography>
                  ) : studioTarget({ fieldPath: "custom.description", label: "Descripción", muted: true })}
                  {custom.ctaLabel && (custom.ctaUrl || studioMode) ? (
                    <Button data-landing-field="custom.ctaLabel" data-landing-element-type="button" data-landing-edit-label="Editar botón" variant="contained" sx={{ alignSelf: customCentered ? "center" : customContentRight ? "flex-end" : "flex-start", textTransform: "none", bgcolor: customVisual.cta.backgroundColor, color: customVisual.cta.textColor, border: `${customVisual.cta.borderWidth}px solid ${customVisual.cta.borderColor}`, borderRadius: `${customVisual.cta.borderRadius}px`, fontWeight: customVisual.cta.fontWeight, boxShadow: customShadow(customVisual.cta), "&:hover": { bgcolor: customVisual.cta.hoverBackgroundColor, color: customVisual.cta.hoverTextColor }, "&:focus-visible": { outline: `3px solid ${customVisual.cta.focusColor}`, outlineOffset: 2 } }}>{custom.ctaLabel}</Button>
                  ) : studioTarget({ fieldPath: "custom.ctaLabel", label: "Botón", muted: !custom.ctaUrl })}
                </Stack>
                {customMediaVisible && !customCentered && customContentLeft ? media : null}
                {customMediaVisible && customCentered ? <Box>{media}</Box> : null}
              </Box>
            );
          })()}
        </SectionFrame>
      ) : null}

      {segmentAllowed("imageCarousel") && sections.showImageCarousel !== false && (imageCarouselHasContent || studioMode) ? (
        <SectionFrame
          sectionId={layoutInstanceId || "imageCarousel"}
          sectionType="imageCarousel"
          layoutOrder={orderFor("imageCarousel")}
          activeSection={activeSection}
          studioMode={studioMode}
          onSelectSection={onSelectSection}
          onSelectElement={onSelectElement}
          selectedElement={selectedElement}
          onOpenSegmentTool={onOpenSegmentTool}
          onToggleSection={onToggleSection} onRemoveSection={onRemoveSection}
          device={previewDevice.id}
          sx={{
            ...segmentCssVariables(theme, "imageCarousel"),
            px: responsive({ xs: 2, sm: 3, md: 6 }, 2),
            py: responsive({ xs: 4.5, md: 8 }, 4.5),
            bgcolor: imageCarousel.shellStyle?.backgroundColor || "#FFFFFF",
          }}
        >
          <Stack spacing={0.7} sx={{ mb: imageCarouselItems.length || studioMode ? 2.5 : 0 }}>
            {imageCarousel.eyebrow ? <Typography data-landing-field="imageCarousel.eyebrow" data-landing-element-type={studioMode ? "text" : undefined} data-landing-edit-label={studioMode ? "Editar texto superior" : undefined} variant="overline" sx={{ color: imageCarousel.eyebrowStyle?.color || "#F35A3A", fontSize: finite(imageCarousel.eyebrowStyle?.fontSize, 12), fontWeight: finite(imageCarousel.eyebrowStyle?.fontWeight, 900), lineHeight: finite(imageCarousel.eyebrowStyle?.lineHeight, 1.4), letterSpacing: finite(imageCarousel.eyebrowStyle?.letterSpacing, 1.2) }}>{imageCarousel.eyebrow}</Typography> : studioTarget({ fieldPath: "imageCarousel.eyebrow", label: "Texto superior", muted: true })}
            {imageCarousel.title ? <Typography data-landing-field="imageCarousel.title" data-landing-element-type={studioMode ? "text" : undefined} data-landing-edit-label={studioMode ? "Editar título" : undefined} sx={{ color: imageCarousel.titleStyle?.color || "#19242F", fontSize: responsive({ xs: finite(imageCarousel.titleStyle?.fontSizeMobile, 30) * titleWordFitScale(imageCarousel.title), md: finite(imageCarousel.titleStyle?.fontSizeDesktop, 40) * titleWordFitScale(imageCarousel.title) }, 30), lineHeight: finite(imageCarousel.titleStyle?.lineHeight, 1.08), fontWeight: finite(imageCarousel.titleStyle?.fontWeight, 900), letterSpacing: finite(imageCarousel.titleStyle?.letterSpacing, -.4), overflowWrap: "normal", wordBreak: "normal", hyphens: "none" }}>{capitalizeSectionTitle(imageCarousel.title)}</Typography> : studioTarget({ fieldPath: "imageCarousel.title", label: "Título" })}
            {imageCarousel.subtitle ? <Typography data-landing-field="imageCarousel.subtitle" data-landing-element-type={studioMode ? "text" : undefined} data-landing-edit-label={studioMode ? "Editar descripción" : undefined} sx={{ color: imageCarousel.subtitleStyle?.color || "#5B5D73", fontSize: finite(imageCarousel.subtitleStyle?.fontSize, 16), fontWeight: finite(imageCarousel.subtitleStyle?.fontWeight, 400), maxWidth: finite(imageCarousel.subtitleStyle?.maxWidth, 780), lineHeight: finite(imageCarousel.subtitleStyle?.lineHeight, 1.65) }}>{imageCarousel.subtitle}</Typography> : studioTarget({ fieldPath: "imageCarousel.subtitle", label: "Descripción", muted: true })}
          </Stack>
          {imageCarouselItems.length ? (
            <ImageCarouselCanvasPreview
              items={imageCarouselItems}
              config={imageCarousel}
              mobileLayout={mobileLayout}
              responsive={responsive}
              studioMode={studioMode}
            />
          ) : (
            <Box sx={{ minHeight: 220, borderRadius: `${finite(imageCarousel.mediaStyle?.borderRadius, 28)}px`, border: "1px dashed rgba(25,36,47,.20)", display: "grid", placeItems: "center", bgcolor: "rgba(25,36,47,.025)" }}>
              <Stack spacing={1} alignItems="center">
                <Typography sx={{ color: "rgba(25,36,47,.58)", fontWeight: 800 }}>Todavía no hay imágenes</Typography>
                {studioMode ? studioTarget({ fieldPath: "imageCarousel.items", label: "Agregar imágenes" }) : null}
              </Stack>
            </Box>
          )}
          {studioMode && imageCarouselItems.length ? <Stack direction="row" spacing={0.8} sx={{ mt: 1.2 }}>{studioTarget({ fieldPath: "imageCarousel.items", label: "Administrar imágenes", count: imageCarouselItems.length })}</Stack> : null}
        </SectionFrame>
      ) : null}

      {segmentAllowed("testimonials") && sections.showTestimonials !== false && (testimonialsHasContent || studioMode) ? (
        <SectionFrame
          sectionId={layoutInstanceId || "testimonials"}
          sectionType="testimonials"
          layoutOrder={orderFor("testimonials")}
          activeSection={activeSection}
          studioMode={studioMode}
          onSelectSection={onSelectSection}
          onSelectElement={onSelectElement}
          selectedElement={selectedElement}
          onOpenSegmentTool={onOpenSegmentTool}
          onToggleSection={onToggleSection} onRemoveSection={onRemoveSection}
          device={previewDevice.id}
          sx={{ ...segmentCssVariables(theme, "testimonials"), px: horizontalPadding, py: responsive({ xs: 4, md: 7 }, 4), bgcolor: testimonials.shellStyle?.backgroundColor || "#EEF0F5" }}
        >
          <Stack spacing={0.7} sx={{ mb: testimonialItems.length || studioMode ? 2.2 : 0 }}>
            {testimonials.eyebrow ? <Typography data-landing-field="testimonials.eyebrow" data-landing-element-type={studioMode ? "text" : undefined} data-landing-edit-label={studioMode ? "Editar texto superior" : undefined} variant="overline" sx={{ color: testimonials.eyebrowStyle?.color || "#F35A3A", fontSize: finite(testimonials.eyebrowStyle?.fontSize, 12), fontWeight: finite(testimonials.eyebrowStyle?.fontWeight, 900), lineHeight: finite(testimonials.eyebrowStyle?.lineHeight, 1.4), letterSpacing: finite(testimonials.eyebrowStyle?.letterSpacing, 1.2) }}>{testimonials.eyebrow}</Typography> : studioTarget({ fieldPath: "testimonials.eyebrow", label: "Texto superior", muted: true })}
            {testimonials.title ? <Typography data-landing-field="testimonials.title" data-landing-element-type={studioMode ? "text" : undefined} data-landing-edit-label={studioMode ? "Editar título" : undefined} sx={{ color: testimonials.titleStyle?.color || "#19242F", fontSize: responsive({ xs: finite(testimonials.titleStyle?.fontSizeMobile, 30) * titleWordFitScale(testimonials.title), md: finite(testimonials.titleStyle?.fontSizeDesktop, 40) * titleWordFitScale(testimonials.title) }, 30), lineHeight: finite(testimonials.titleStyle?.lineHeight, 1.08), letterSpacing: finite(testimonials.titleStyle?.letterSpacing, -.4), fontWeight: finite(testimonials.titleStyle?.fontWeight, 900), overflowWrap: "normal", wordBreak: "normal", hyphens: "none" }}>{capitalizeSectionTitle(testimonials.title)}</Typography> : studioTarget({ fieldPath: "testimonials.title", label: "Título" })}
            {testimonials.subtitle ? <Typography data-landing-field="testimonials.subtitle" data-landing-element-type={studioMode ? "text" : undefined} data-landing-edit-label={studioMode ? "Editar descripción" : undefined} sx={{ color: testimonials.subtitleStyle?.color || "#5B5D73", fontSize: finite(testimonials.subtitleStyle?.fontSize, 16), fontWeight: finite(testimonials.subtitleStyle?.fontWeight, 400), lineHeight: finite(testimonials.subtitleStyle?.lineHeight, 1.65), maxWidth: finite(testimonials.subtitleStyle?.maxWidth, 780) }}>{testimonials.subtitle}</Typography> : studioTarget({ fieldPath: "testimonials.subtitle", label: "Descripción", muted: true })}
          </Stack>
          {testimonialItems.length ? (
            <Box sx={{ display: "grid", gridTemplateColumns: responsive({ xs: "minmax(0,1fr)", sm: "repeat(2,minmax(0,1fr))", md: `repeat(${Math.min(3, testimonialItems.length)},minmax(0,1fr))` }, "minmax(0,1fr)"), gap: 2 }}>
              {testimonialItems.map(({ item, index, avatarUrl }) => (
                <Paper key={item?.id || index} elevation={0} data-landing-field={`testimonials.items.${index}`} data-landing-element-type={studioMode ? "collection-item" : undefined} data-landing-edit-label={studioMode ? `Editar testimonio ${index + 1}` : undefined} sx={{ p: responsive({ xs: finite(testimonials.cardStyle?.paddingMobile, 18) / 8, md: finite(testimonials.cardStyle?.paddingDesktop, 24) / 8 }, 2.25), borderRadius: `${finite(testimonials.cardStyle?.borderRadius, 24)}px`, bgcolor: testimonials.cardStyle?.backgroundColor || "#FFFFFF", border: `${finite(testimonials.cardStyle?.borderWidth, 1)}px solid ${testimonials.cardStyle?.borderColor || "rgba(25,36,47,.10)"}`, boxShadow: testimonials.cardStyle?.boxShadow || "none", minWidth: 0, transition: "transform 160ms ease, background-color 160ms ease, border-color 160ms ease, box-shadow 160ms ease", "&:hover": { bgcolor: testimonials.cardStyle?.hoverBackgroundColor || "#FFFFFF", borderColor: testimonials.cardStyle?.hoverBorderColor || "#F35A3A", boxShadow: testimonials.cardStyle?.hoverBoxShadow || "none", transform: `translateY(${finite(testimonials.cardStyle?.hoverTranslateY, -2)}px)` } }}>
                  <Typography aria-hidden sx={{ color: testimonials.accentStyle?.color || "#F35A3A", fontSize: finite(testimonials.accentStyle?.fontSize, 46), lineHeight: finite(testimonials.accentStyle?.lineHeight, .8), fontWeight: finite(testimonials.accentStyle?.fontWeight, 900) }}>“</Typography>
                  {item?.quote ? <Typography data-landing-field={`testimonials.items.${index}.quote`} data-landing-element-type={studioMode ? "text" : undefined} data-landing-edit-label={studioMode ? "Editar testimonio" : undefined} sx={{ mt: 1.2, color: testimonials.quoteStyle?.color || "#19242F", fontSize: finite(testimonials.quoteStyle?.fontSize, 16), fontWeight: finite(testimonials.quoteStyle?.fontWeight, 400), lineHeight: finite(testimonials.quoteStyle?.lineHeight, 1.7), whiteSpace: "pre-line" }}>{item.quote}</Typography> : null}
                  <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mt: 2.2 }}>
                    <Avatar data-landing-field={`testimonials.items.${index}.avatarUrl`} data-landing-element-type={studioMode ? "image" : undefined} data-landing-edit-label={studioMode ? "Editar foto" : undefined} src={avatarUrl || undefined} alt={item?.avatarAlt || item?.name || ""} sx={{ width: finite(testimonials.avatarStyle?.size, 48), height: finite(testimonials.avatarStyle?.size, 48), bgcolor: testimonials.avatarStyle?.backgroundColor || "#F35A3A", color: testimonials.avatarStyle?.color || "#FFFFFF", border: `${finite(testimonials.avatarStyle?.borderWidth, 0)}px solid ${testimonials.avatarStyle?.borderColor || "transparent"}`, boxShadow: testimonials.avatarStyle?.boxShadow || "none" }}>{!avatarUrl ? String(item?.name || "?").slice(0, 1).toUpperCase() : null}</Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      {item?.name ? <Typography data-landing-field={`testimonials.items.${index}.name`} data-landing-element-type={studioMode ? "text" : undefined} data-landing-edit-label={studioMode ? "Editar nombre" : undefined} sx={{ color: testimonials.nameStyle?.color || "#19242F", fontSize: finite(testimonials.nameStyle?.fontSize, 16), fontWeight: finite(testimonials.nameStyle?.fontWeight, 900), lineHeight: finite(testimonials.nameStyle?.lineHeight, 1.3) }}>{item.name}</Typography> : null}
                      {item?.role ? <Typography data-landing-field={`testimonials.items.${index}.role`} data-landing-element-type={studioMode ? "text" : undefined} data-landing-edit-label={studioMode ? "Editar referencia" : undefined} variant="body2" sx={{ color: testimonials.roleStyle?.color || "#5B5D73", fontSize: finite(testimonials.roleStyle?.fontSize, 14), fontWeight: finite(testimonials.roleStyle?.fontWeight, 400), lineHeight: finite(testimonials.roleStyle?.lineHeight, 1.4) }}>{item.role}</Typography> : null}
                    </Box>
                  </Stack>
                </Paper>
              ))}
            </Box>
          ) : (
            <Box sx={{ minHeight: 170, borderRadius: `${finite(testimonials.cardStyle?.borderRadius, 24)}px`, border: `${Math.max(1, finite(testimonials.cardStyle?.borderWidth, 1))}px dashed ${testimonials.cardStyle?.borderColor || "rgba(25,36,47,.18)"}`, display: "grid", placeItems: "center", bgcolor: testimonials.cardStyle?.backgroundColor || "#FFFFFF" }}>
              <Stack spacing={1} alignItems="center">
                <Typography sx={{ color: testimonials.roleStyle?.color || "#5B5D73", fontSize: finite(testimonials.roleStyle?.fontSize, 14), fontWeight: finite(testimonials.roleStyle?.fontWeight, 400), lineHeight: finite(testimonials.roleStyle?.lineHeight, 1.4), fontWeight: 800 }}>Todavía no hay testimonios</Typography>
                {studioMode ? studioTarget({ fieldPath: "testimonials.items", label: "Agregar testimonios" }) : null}
              </Stack>
            </Box>
          )}
          {studioMode && testimonialItems.length ? <Stack direction="row" spacing={0.8} sx={{ mt: 1.2 }}>{studioTarget({ fieldPath: "testimonials.items", label: "Administrar testimonios", count: testimonialItems.length })}</Stack> : null}
        </SectionFrame>
      ) : null}

      {segmentAllowed("contact") && sections.showContact !== false && (contactHasContent || studioMode) ? (
        <SectionFrame
          sectionId={layoutInstanceId || "contact"}
          sectionType="contact"
          layoutOrder={orderFor("contact")}
          activeSection={activeSection}
          studioMode={studioMode}
          onSelectSection={onSelectSection}
          onSelectElement={onSelectElement}
          selectedElement={selectedElement}
          onOpenSegmentTool={onOpenSegmentTool}
          onToggleSection={onToggleSection} onRemoveSection={onRemoveSection}
          device={previewDevice.id}
          sx={{
            ...segmentCssVariables(theme, "contact"),
            px: horizontalPadding,
            py: responsive({ xs: px(finite(spacing.xl, 32) * contactSpacingScale, 32), md: px(finite(spacing.section, 72) * contactSpacingScale, 72) }, px(finite(spacing.xl, 32) * contactSpacingScale, 32)),
            color: contactVisual.subtitleColor,
            backgroundColor: contactVisual.backgroundColor,
            backgroundImage: `radial-gradient(circle at 0% 0%, ${colorWithAlpha(contactVisual.decorativeAccentColor, 0.2)} 0%, transparent 42%)`,
          }}
        >
          <Box sx={{ display: "grid", gridTemplateColumns: responsive({ xs: "1fr", md: contactHasMedia || studioMode ? "1.35fr .65fr" : "1fr" }, "1fr"), gap: responsive({ xs: 2, md: 3 }, 2), alignItems: "stretch" }}>
            <Box sx={{ minWidth: 0, alignSelf: "center" }}>
              {contact.title ? (
                <Typography data-landing-field="contact.title" data-landing-edit-label="Editar título de contacto" sx={{ color: contactVisual.titleColor, fontSize: responsive({ xs: 30 * (contactTitleScale * titleWordFitScale(contact.title)), md: 34 * (contactTitleScale * titleWordFitScale(contact.title)) }, 30 * (contactTitleScale * titleWordFitScale(contact.title))), overflowWrap: "normal", wordBreak: "normal", hyphens: "none", fontWeight: "var(--wp-segment-title-weight)", letterSpacing: "var(--wp-segment-letter-spacing)" }}>{capitalizeSectionTitle(contact.title)}</Typography>
              ) : studioTarget({ fieldPath: "contact.title", label: "Título", muted: true })}
              {contact.subtitle ? (
                <Typography data-landing-field="contact.subtitle" data-landing-edit-label="Editar texto de apoyo" sx={{ mt: contact.title ? .8 : 0, color: contactVisual.subtitleColor }}>{contact.subtitle}</Typography>
              ) : studioTarget({ fieldPath: "contact.subtitle", label: "Texto de apoyo", muted: true })}

              <Stack direction={responsive({ xs: "column", sm: "row" }, "column")} spacing={1.2} useFlexGap flexWrap="wrap" sx={{ mt: contact.title || contact.subtitle ? 2 : 0 }}>
                {whatsappDigits && contact.whatsappCtaLabel ? (
                  <Button
                    data-landing-field="contact.whatsappCtaLabel"
                    data-landing-element-type="button"
                    data-landing-edit-label="Editar botón de WhatsApp"
                    variant="contained"
                    startIcon={<WhatsAppIcon />}
                    sx={{ bgcolor: "#25D366", color: "#FFFFFF", textTransform: "none", boxShadow: shadows.sm, "&:hover": { bgcolor: "#1FB758" } }}
                  >
                    {contact.whatsappCtaLabel}
                  </Button>
                ) : studioTarget({ fieldPath: "contact.whatsappCtaLabel", label: "Botón de WhatsApp", muted: true })}
                {contact.detailsCtaLabel && contact.detailsCtaUrl ? (
                  <Button
                    data-landing-field="contact.detailsCtaLabel"
                    data-landing-element-type="button"
                    data-landing-edit-label="Editar botón de detalles"
                    variant="outlined"
                    sx={{ color: contactVisual.detailsCtaTextColor, borderColor: "rgba(255,255,255,.7)", textTransform: "none" }}
                  >
                    {contact.detailsCtaLabel}
                  </Button>
                ) : studioTarget({ fieldPath: "contact.detailsCtaLabel", label: "Botón de detalles", muted: true })}
              </Stack>

              {contactHasDetails ? (
                <Stack
                  direction={responsive({ xs: "column", sm: "row" }, "column")}
                  spacing={0.9}
                  useFlexGap
                  flexWrap="wrap"
                  data-landing-element-id="contact.business-data"
                  data-landing-element-type="complex"
                  data-landing-edit-label="Editar datos del negocio"
                  sx={{ mt: 1.2, p: studioMode ? 0.7 : 0, borderRadius: 2 }}
                >
                  {businessAddress ? (
                    <Stack direction="row" spacing={0.8} alignItems="flex-start" sx={{ minWidth: 0 }}>
                      <PlaceIcon fontSize="small" sx={{ mt: 0.15, color: contactVisual.addressIconColor }} />
                      <Typography data-landing-field="business.address" sx={{ color: contactVisual.addressTextColor, overflowWrap: "anywhere" }}>{businessAddress}</Typography>
                    </Stack>
                  ) : null}
                  {business.schedule ? (
                    <Stack direction="row" spacing={0.8} alignItems="flex-start" sx={{ minWidth: 0 }}>
                      <ScheduleOutlinedIcon fontSize="small" sx={{ mt: 0.15, color: contactVisual.scheduleIconColor }} />
                      <Typography data-landing-field="business.schedule" sx={{ color: contactVisual.scheduleTextColor, overflowWrap: "anywhere" }}>{business.schedule}</Typography>
                    </Stack>
                  ) : null}
                  {business.phone ? (
                    <Stack direction="row" spacing={0.8} alignItems="center" sx={{ minWidth: 0 }}>
                      <PhoneOutlinedIcon fontSize="small" sx={{ color: contactVisual.phoneIconColor }} />
                      <Typography data-landing-field="business.phone" sx={{ color: contactVisual.phoneTextColor }}>{business.phone}</Typography>
                    </Stack>
                  ) : null}
                  {business.email ? (
                    <Stack direction="row" spacing={0.8} alignItems="center" sx={{ minWidth: 0 }}>
                      <EmailOutlinedIcon fontSize="small" sx={{ color: contactVisual.emailIconColor }} />
                      <Typography data-landing-field="business.email" sx={{ color: contactVisual.emailTextColor, overflowWrap: "anywhere" }}>{business.email}</Typography>
                    </Stack>
                  ) : null}
                </Stack>
              ) : studioMode ? (
                <Stack sx={{ mt: 1.2 }}>
                  {studioTarget({ elementId: "contact.business-data", label: "Datos del negocio", count: 0, muted: true })}
                </Stack>
              ) : null}

              {studioMode ? (
                <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" sx={{ mt: 1.1 }}>
                  {studioTarget({ elementId: "contact.socials", label: "Redes sociales", count: contactSocialCount, muted: contactSocialCount === 0 })}
                  <Chip size="small" label="Datos compartidos" variant="outlined" sx={{ color: "inherit", borderColor: "currentColor", opacity: .72 }} />
                </Stack>
              ) : null}
            </Box>

            {contactMapUrl ? (
              <Box
                data-landing-element-id={studioMode ? "contact.map" : undefined}
                data-landing-element-type={studioMode ? "complex" : undefined}
                data-landing-edit-label={studioMode ? "Editar mapa e imagen" : undefined}
                sx={{
                  width: "100%",
                  minHeight: responsive({ xs: 220 * contactImageScale, md: 300 * contactImageScale }, 220 * contactImageScale),
                  maxHeight: 430 * contactImageScale,
                  height: "100%",
                  overflow: "hidden",
                  borderRadius: px(borders.radiusLg, 18),
                  border: "1px solid rgba(255,255,255,.18)",
                  boxShadow: shadows.md,
                  bgcolor: contactVisual.imageBackgroundColor,
                }}
              >
                <Box
                  component="iframe"
                  title={contact.mapTitle || "Ubicación del negocio"}
                  src={contactMapUrl}
                  loading="lazy"
                  allowFullScreen
                  referrerPolicy="no-referrer-when-downgrade"
                  sx={{ width: "100%", height: "100%", minHeight: "inherit", border: 0, display: "block", pointerEvents: studioMode ? "none" : "auto" }}
                />
              </Box>
            ) : contactImage ? (
              <SafeCanvasImage
                src={contactImage}
                alt={contact.mapTitle || ""}
                landingElementId={studioMode ? "contact.map" : ""}
                landingElementType="complex"
                editLabel="Editar mapa e imagen"
                sx={{ width: "100%", minHeight: responsive({ xs: 220 * contactImageScale, md: 300 * contactImageScale }, 220 * contactImageScale), maxHeight: 430 * contactImageScale, height: "100%", objectFit: "cover", objectPosition: contactImagePosition, borderRadius: px(borders.radiusLg, 18), border: "1px solid rgba(255,255,255,.18)", boxShadow: shadows.md, bgcolor: contactVisual.imageBackgroundColor }}
              />
            ) : studioMode ? (
              <Box
                data-landing-element-id="contact.map"
                data-landing-element-type="complex"
                data-landing-edit-label="Editar mapa e imagen"
                sx={{
                  minHeight: responsive({ xs: 170, md: 260 }, 170),
                  display: "grid",
                  placeItems: "center",
                  p: 2,
                  border: "1px dashed rgba(255,255,255,.5)",
                  borderRadius: px(borders.radiusLg, 18),
                  bgcolor: contactVisual.imageBackgroundColor,
                  textAlign: "center",
                }}
              >
                <Stack spacing={0.8} alignItems="center">
                  <Typography sx={{ fontWeight: 900 }}>Mapa e imagen</Typography>
                  <Typography variant="caption" sx={{ opacity: .72 }}>Tocá para configurar la imagen, el encuadre y el mapa.</Typography>
                  {business.mapEmbedUrl ? <Chip size="small" label="Mapa conectado" /> : <Chip size="small" label="Sin configurar" variant="outlined" sx={{ color: "inherit", borderColor: "currentColor" }} />}
                </Stack>
              </Box>
            ) : null}
          </Box>
        </SectionFrame>
      ) : null}

      {segmentAllowed("footer") && sections.showFooter !== false && (footerHasVisibleContent || studioMode) ? (
        <SectionFrame
          sectionId="footer"
          layoutOrder={orderFor("footer")}
          activeSection={activeSection}
          studioMode={studioMode}
          onSelectSection={onSelectSection}
          onSelectElement={onSelectElement}
          selectedElement={selectedElement}
          onOpenSegmentTool={onOpenSegmentTool}
          onToggleSection={onToggleSection} onRemoveSection={onRemoveSection}
          device={previewDevice.id}
          backgroundSx={sectionBackgroundSx("footer")}
          sx={{
            ...segmentCssVariables(theme, "footer"),
            ...elementColorSxForSection("footer"),
            px: horizontalPadding,
            py: px(finite(spacing.lg, 24) * footerSpacingScale, 24),
            bgcolor: footerStyle === "style-2" ? footerColors.surface : footerStyle === "style-3" ? "#10171e" : footerColors.secondary,
            color: footerStyle === "style-2" ? footerColors.text : footerColors.onSecondary,
            borderTop: footerStyle === "style-2" ? cardBorder : "none",
          }}
        >
          <Stack spacing={2} alignItems={footerStyle === "style-3" ? "center" : "stretch"} textAlign={footerStyle === "style-3" ? "center" : "left"}>
            {studioMode ? (
              <Stack direction="row" spacing={0.65} useFlexGap flexWrap="wrap" justifyContent={footerStyle === "style-3" ? "center" : "flex-start"}>
                {footer.showLogo === false || !logo ? studioTarget({ elementId: "footer.logo", label: "Logo", muted: true }) : null}
                {!footerHasBrand ? studioTarget({ elementId: "footer.brand", label: "Marca", muted: true }) : null}
                {!footerHasNavigation ? studioTarget({ elementId: "footer.navigation", label: "Navegación", muted: true }) : null}
                {!footerHasContact ? studioTarget({ elementId: "footer.contact", label: "Contacto", muted: true }) : null}
                {navigation.showSocialLinks === false || !footerSocialLabels.length ? studioTarget({ elementId: "footer.socials", label: "Redes", muted: true }) : null}
                {!footerCopyright ? studioTarget({ fieldPath: "footer.copyright", label: "Copyright", muted: true }) : null}
              </Stack>
            ) : null}

            <Stack direction={footerStyle === "style-3" ? "column" : responsive({ xs: "column", md: "row" }, "column")} justifyContent="space-between" alignItems={footerStyle === "style-3" ? "center" : "flex-start"} spacing={3} sx={{ width: "100%" }}>
              {footerHasBrand ? (
                <Stack
                  direction={footerStyle === "style-3" ? "column" : "row"}
                  alignItems={footerStyle === "style-3" ? "center" : "flex-start"}
                  spacing={1.1}
                  data-landing-element-id="footer.brand"
                  data-landing-element-type="complex"
                  data-landing-edit-label="Editar marca del pie de página"
                  sx={{ minWidth: 0, flex: "1 1 260px" }}
                >
                  {footer.showLogo !== false && logo ? (
                    <Box
                      data-landing-element-id="footer.logo"
                      data-landing-element-type="image"
                      data-landing-edit-label="Editar logo del pie de página"
                      sx={{ display: "inline-flex", flexShrink: 0 }}
                    >
                      <SafeCanvasImage
                        src={logo}
                        alt=""
                        monochromeColor={footerLogoColor}
                        sx={footerLogoMaxHeight !== null
                          ? {
                              display: "block",
                              width: "auto",
                              maxWidth: "100%",
                              height: footerLogoMaxHeight,
                              maxHeight: footerLogoMaxHeight,
                              objectFit: "contain",
                              objectPosition: footerLogoPosition,
                              flexShrink: 0,
                            }
                          : { width: 42 * footerImageScale, height: 42 * footerImageScale, objectFit: "contain", objectPosition: footerLogoPosition, flexShrink: 0 }}
                      />
                    </Box>
                  ) : null}
                  {business.name || footerBrandTagline ? <Box sx={{ minWidth: 0 }}>
                    {business.name ? <Typography noWrap sx={{ fontSize: `${finite(typography.baseFontSize, 16) * footerTitleScale}px`, fontWeight: "var(--wp-segment-title-weight)", letterSpacing: "var(--wp-segment-letter-spacing)" }}>{business.name}</Typography> : null}
                    {footerBrandTagline ? <Typography variant="caption" sx={{ display: "block", mt: business.name ? 0.35 : 0, opacity: .72, whiteSpace: "pre-line" }}>{footerBrandTagline}</Typography> : null}
                  </Box> : null}
                </Stack>
              ) : null}

              {footerHasNavigation ? (
                <Box
                  data-landing-element-id="footer.navigation"
                  data-landing-element-type="complex"
                  data-landing-edit-label="Editar navegación del pie de página"
                  sx={{ flex: "1 1 280px", minWidth: 0 }}
                >
                  {footer.navigationTitle ? <Typography variant="caption" sx={{ display: "block", mb: 0.7, fontWeight: 900 }}>{footer.navigationTitle}</Typography> : null}
                  <Stack direction="row" spacing={1.4} useFlexGap flexWrap="wrap" justifyContent={footerStyle === "style-3" ? "center" : "flex-start"}>
                    {footerHomeLabel ? <Typography variant="caption">{footerHomeLabel}</Typography> : null}
                    {gastronomicHome ? <Typography variant="caption">Carta</Typography> : null}
                    {productsEnabled && navigation.showProductsLink !== false && footerProductsLabel ? <Typography variant="caption">{footerProductsLabel}</Typography> : null}
                    {footerAboutVisible && navigation.aboutLabel ? <Typography variant="caption">{navigation.aboutLabel}</Typography> : null}
                    {footerHighlightsVisible && navigation.highlightsLabel ? <Typography variant="caption">{navigation.highlightsLabel}</Typography> : null}
                    {footerContactVisible && footerContactLabel ? <Typography variant="caption">{footerContactLabel}</Typography> : null}
                  </Stack>
                </Box>
              ) : null}

              {footerHasContact ? (
                <Box
                  data-landing-element-id="footer.contact"
                  data-landing-element-type="complex"
                  data-landing-edit-label="Editar contacto del pie de página"
                  sx={{ flex: "0 1 220px", minWidth: 0 }}
                >
                  {footer.contactTitle ? <Typography variant="caption" sx={{ display: "block", mb: 0.7, fontWeight: 900 }}>{footer.contactTitle}</Typography> : null}
                  {businessAddress ? <Typography variant="caption" sx={{ display: "block", opacity: .72, overflowWrap: "anywhere" }}>{businessAddress}</Typography> : null}
                  {business.schedule ? <Typography variant="caption" sx={{ display: "block", mt: 0.25, opacity: .72, overflowWrap: "anywhere" }}>{business.schedule}</Typography> : null}
                  {business.email ? <Typography variant="caption" sx={{ display: "block", mt: 0.25, opacity: .72, overflowWrap: "anywhere" }}>{business.email}</Typography> : null}
                  {business.phone ? <Typography variant="caption" sx={{ display: "block", mt: 0.25, opacity: .72 }}>{business.phone}</Typography> : null}
                </Box>
              ) : null}

              {footerSocialLabels.length ? (
                <Box
                  data-landing-element-id="footer.socials"
                  data-landing-element-type="complex"
                  data-landing-edit-label="Editar redes sociales del pie de página"
                  sx={{ flex: "0 1 220px", minWidth: 0 }}
                >
                  <Typography variant="caption" sx={{ display: "block", mb: 0.7, fontWeight: 900 }}>{navigation.socialTitle || "Redes sociales"}</Typography>
                  <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" justifyContent={footerStyle === "style-3" ? "center" : "flex-start"}>
                    {footerSocialLabels.map((label) => <Chip key={label} size="small" label={label} sx={{ maxWidth: "100%", bgcolor: footerStyle === "style-2" ? background : "rgba(255,255,255,.1)", color: "inherit" }} />)}
                  </Stack>
                </Box>
              ) : null}
            </Stack>

            {footerCopyright ? (
              <Typography data-landing-field="footer.copyright" data-landing-element-type="text" data-landing-edit-label="Editar copyright" variant="caption" sx={{ width: "100%", pt: 1.2, borderTop: footerStyle === "style-2" ? `1px solid ${footerColors.divider}` : "1px solid rgba(255,255,255,.12)", opacity: .65 }}>
                {footerCopyright}
              </Typography>
            ) : null}
          </Stack>
        </SectionFrame>
      ) : null}

      {!bare && !onlySection ? layoutSegments
        .filter((segment) => !isCanonicalLandingSegment(segment) && segment.visible !== false)
        .map((segment) => {
          const instanceEditable = !isStructuralLandingType(segment.type);
          return (
            <LandingCanvas
              key={segment.id}
              form={applyLandingSegmentSnapshot(displayForm, segment)}
              activeSection={studioMode && instanceEditable ? activeSection : ""}
              selectedElement={studioMode && instanceEditable ? selectedElement : null}
              device={device}
              productsEnabled={productsEnabled}
              gastronomicMenuEnabled={gastronomicMenuEnabled}
              studioMode={studioMode && instanceEditable}
              onSelectSection={instanceEditable ? onSelectSection : undefined}
              onSelectElement={instanceEditable ? onSelectElement : undefined}
              onOpenSegmentTool={instanceEditable ? onOpenSegmentTool : undefined}
              onToggleSection={instanceEditable ? onToggleSection : undefined}
              onRemoveSection={instanceEditable ? onRemoveSection : undefined}
              onlySection={segment.type}
              bare
              layoutInstanceId={segment.id}
            />
          );
        }) : null}

      {!bare && !onlySection && (studioMode || (business.whatsapp && sections.showFloatingWhatsapp !== false)) ? (
        <Box
          role="button"
          tabIndex={0}
          data-landing-element-id={studioMode ? "floatingWhatsapp.button" : undefined}
          data-landing-element-type={studioMode ? "complex" : undefined}
          data-landing-edit-label={studioMode ? "Editar WhatsApp flotante" : undefined}
          data-landing-element-selected={studioMode && selectedElement?.sectionId === "floatingWhatsapp" && selectedElement?.elementId === "floatingWhatsapp.button" ? "true" : undefined}
          aria-label={studioMode
            ? `Editar WhatsApp flotante. ${business.whatsapp ? "WhatsApp configurado" : "Falta número"}${sections.showFloatingWhatsapp === false ? ". Oculto" : ""}`
            : (contact.floatingWhatsappAriaLabel || "Contactar por WhatsApp")}
          title={studioMode ? "Editar WhatsApp flotante" : undefined}
          onClick={(event) => {
            if (!studioMode) return;
            event.preventDefault();
            event.stopPropagation();
            onSelectElement?.({
              sectionId: "floatingWhatsapp",
              sectionType: "floatingWhatsapp",
              fieldPath: "",
              elementId: "floatingWhatsapp.button",
              logicalType: "complex",
              previewDevice: device || "desktop",
            });
          }}
          onKeyDown={(event) => {
            if (!studioMode || (event.key !== "Enter" && event.key !== " ")) return;
            event.preventDefault();
            event.stopPropagation();
            onSelectElement?.({
              sectionId: "floatingWhatsapp",
              sectionType: "floatingWhatsapp",
              fieldPath: "",
              elementId: "floatingWhatsapp.button",
              logicalType: "complex",
              previewDevice: device || "desktop",
            });
          }}
          sx={{
            ...floatingWhatsappVisualSx,
            position: "absolute",
            right: 18,
            bottom: 18,
            display: "grid",
            placeItems: "center",
            cursor: studioMode ? "pointer" : "default",
            opacity: studioMode && (sections.showFloatingWhatsapp === false || !business.whatsapp) ? 0.52 : 1,
            outline: studioMode && selectedElement?.sectionId === "floatingWhatsapp" && selectedElement?.elementId === "floatingWhatsapp.button"
              ? "3px solid rgba(243,90,58,.96)"
              : studioMode && (sections.showFloatingWhatsapp === false || !business.whatsapp)
                ? "2px dashed rgba(25,36,47,.42)"
                : "2px solid transparent",
            outlineOffset: 3,
            boxShadow: activeSection === "floatingWhatsapp"
              ? `0 0 0 4px rgba(243,90,58,.24), ${floatingWhatsappStyle.boxShadow}`
              : floatingWhatsappStyle.boxShadow,
            transition: "opacity .15s ease, outline-color .15s ease, background-color .15s ease, color .15s ease, border-color .15s ease, box-shadow .15s ease, transform .15s ease",
            "&:hover": {
              ...floatingWhatsappVisualSx["&:hover"],
              ...(studioMode ? { opacity: 1, outlineColor: "rgba(243,90,58,.86)" } : {}),
            },
            "&:focus-visible": studioMode ? { opacity: 1, outlineColor: "rgba(243,90,58,1)" } : undefined,
            zIndex: 4,
          }}
        >
          <WhatsAppIcon />
        </Box>
      ) : null}
    </Box>
  );
}
